// The in-match HUD (docs/platform/UiFidelity.md): the original's wmd_hud scene drawn by the IP2
// runtime (client/src/ui/ip2) on a canvas over the 3-D view and driven by a port of
// UICallback_MainGame_OnIdle 0xea84c0 (UICB_Scene_MainGame.cpp): the team panels, turn timer
// and round clock, wind, commentary, hot seat panel and the weapons panel (cIP2_WP_Frame);
// the worm labels (WormHUDPanel), damage numbers (WormHUDDamage) and the current worm's arrow
// (WormHUDCurrentArrow) at WormHUDMan::WorldToHUD positions. The HUD's clock is the sim's
// (one UI frame per tick: the original's presented frames at 60 Hz), so a replayed tick
// shows what the original showed at it.
import type { CameraMan } from '../render/camera.ts';
import { audio } from './audio.ts';
import { TimerSounds, fanfareSounds } from './uisounds.ts';
import type { Renderer } from '../render/renderer.ts';
import { UiXom, clipDuration } from '../render/uixom.ts';
import {
  FLOW_ANY_INPUT, FLOW_DAMAGE_APPLIED, FLOW_END_OF_HOTSEAT, FLOW_RETREAT_REQUESTED, FLOW_ROUND_DONE, FLOW_TELEPORTING, FLOW_TURN_DONE,
  decodeCraft, decodeHud, decodePanel, decodeRecipes, type CraftChecks, type HudComment, type HudStep, type HudTeam, type HudView, type PanelInfo,
  type Recipe, ENTER_ACTIVE, ENTER_DEAD, ENTER_DROWNING, ENTER_JETPACK, ENTER_LABEL, ENTER_SURRENDERED,
} from '../sim/hudview.ts';
import { WORM_GRAVESTONE, decodePhase, type FrameView, type WormView } from '../sim/view.ts';
import { Ip2Painter, widgetTextures } from '../ui/ip2/draw.ts';
import { AimWidgets, hiddenFromLocal } from './widgets.ts';
import { replaceControlTokens } from '../ui/ip2/controls.ts';
import { DESIGN_H, DESIGN_W, Item, Scene, T_CRAFT, T_TAB, T_TEX, T_TXT, T_WPCELL, T_XOM, UiData, xomDefault, type Rgba } from '../ui/ip2/ip2.ts';

/** A match team (the lobby's MatchTeam): `wins` = its lobby slot's round wins in the room's
 * earlier matches (the server's NUI_Cache team wins, protocol `MatchTeam::wins`); `user` = the
 * lobby name of the member who plays it (the original's `UserMan::MapTeamToSibling` gamer or
 * `MapTeamToRival` rival of an online match), null for an AI team and offline. */
export type TeamInfo = { name: string; colour: number; mine: boolean; wins: number; user: string | null };

/** A user name as the In_Game_Team NDS primers show it (`NDSPRIMER_Gamer_Name` 0xec60c0,
 * `Team_or_User` 0xec6500, `User_or_Prepare` 0xec6810): more than 16 bytes of UTF-8 keep their
 * first 16 characters (`XString::ChopUTF8`) and get "...". */
function primerUserName(name: string): string {
  return new TextEncoder().encode(name).length > 16 ? `${[...name].slice(0, 16).join('')}...` : name;
}

/** One observed sim step's HUD inputs, decoded (hudview.ts HudStep); `craft`: the current team's
 * crafting menu answers (CraftingMan; undefined for a step the worker did not present: the HUD
 * keeps the last ones); `restored`: the first step after a snapshot restore (the HUD never saw
 * the ticks before it). */
type StepInput = {
  h: HudView; turnFlags: number; currentWorm: number; currentTeam: number; health: number[]; maxHealth: number[]; wind: number; phase: number;
  craft?: CraftChecks | null; restored: boolean;
};
/** A worm's WormHUDPanel: `m_eTransitionState`, `m_fMovePosY` (world units above the label's
 * place) and `m_uWormHealth` (the health it shows, counted towards the worm's by 1 per tick);
 * UpdateTransitionState's vehicle block: `vehicle` = m_CurrentVehicleIndex, `drawEnter` =
 * m_bDrawEnterVehicle, `offset` = m_fVehicleOffsetY, `vx`/`vy` = m_vVehiclePosition (y up),
 * `vid` = m_eVehicleID; GraphicUpdate's `lastVehicle` = m_LastVehicleIndex and `capture`: the
 * prompt's capture (m_EnterVehicleCaptureHandle) exists. */
type WormPanel = {
  state: number; move: number; health: number; tick: number;
  vehicle: number; lastVehicle: number; drawEnter: boolean; offset: number; vx: number; vy: number; vid: number; capture: boolean;
};
const newPanel = (state = 0, move = 0): WormPanel => ({
  state, move, health: -1, tick: -1, vehicle: 0xffffffff, lastVehicle: 0xffffffff, drawEnter: false, offset: 0, vx: 0, vy: 0, vid: 0, capture: false,
});
/** `Worm::GetGraphicalPosition` 0xc55c80 (world units, y up; the frame view's `graphical`): the
 * label, arrow and prompt anchor; in a vehicle the vehicle mesh's graphical position plus the worm
 * offset, else the physics position. */
const graphicalPosition = (w: WormView): [number, number] => [w.graphical[0], w.graphical[1]];
/** `cIP2_2DXom`'s authored camera and composite (the .bkm `xom` section). */
type XomSection = { m_CameraPositionX?: number; m_CameraPositionY?: number; m_CameraPositionZ?: number; m_CompositeMode?: number; m_ParentXOMID?: number; m_ParentNodeName?: string };

/** `TeamColours::g_FlatColour` (TeamColours.cpp static initialiser 0x963c40) by colour index;
 * `MatchSpec::GetTeamColour` = the flat colour of the team's colour index (6: white). */
export const FLAT_COLOURS: [number, number, number][] = [[225, 106, 97], [62, 154, 224], [109, 192, 38], [141, 79, 243], [238, 229, 84], [49, 226, 215], [255, 255, 255]];

// UICallback_MainGame_OnIdle's game states (gUICB_MainGame_DATA.m_eGameState).
const START_INIT = 0, INIT = 1, START_IN_TURN = 2, IN_TURN = 3, START_RETREAT = 4, IN_RETREAT = 5, START_END_TURN = 6,
  IN_END_TURN = 7, START_UPDATE_HEALTH = 8, IN_UPDATE_HEALTH = 9, START_HOT_SEAT = 10, IN_HOT_SEAT = 11, START_END_ROUND = 12,
  IN_END_ROUND = 13;
// Transition states (commentary, the health panel, the weapons panel).
const OFF = 0, TRANS_ON = 1, ON = 2, TRANS_OFF = 3;
// IPE2D_MESSAGE ON_USER_1 / ON_USER_2: the authored events 9 and 10.
const MSG_USER_1 = 9, MSG_USER_2 = 10;
/** IPE2D_MESSAGE ON_CREATE: an item's timeline run when its menu is created. */
const MSG_ON_CREATE = 2;
/** NUI_Cache::m_bShowRoundResults from a step's phase: the sim's Wait(ExitMatch) starts with
 * Goodbye1, where BaseRoundLogic::Update_WaitSaveGame sets the flag. */
function isShowResults(phase: number): boolean {
  const ph = decodePhase(phase);
  return ph.kind === 'Wait' && ph.wait === 'ExitMatch';
}
/** StatsMan's award icons (`gsTeamStatIcons` 0x15b6f00, RMUI texture indices) and titles
 * (`gsTeamStatTitles` 0x1b76d80) by RoundAward. */
const AWARD_ICONS = [452, 257, 174, 258, 263, 260, 261, 451, 262, 274, 259, 449, 229, 450, 266, 264, 265];
const AWARD_TITLES = ['Stat.Award.WormOfMatch', 'Stat.Award.Boring', 'Stat.Award.Accurate', 'Stat.Award.Coward', 'Stat.Award.Liability',
  'Stat.Award.Darksider', 'Stat.Award.Greedy', 'Stat.Award.VehicleHogger', 'Stat.Award.Hider', 'Stat.Award.ProRoper', 'Stat.Award.Crafter',
  'Stat.Award.Quick', 'Stat.Award.Airborne', 'Stat.Award.Showman', 'Stat.Award.PartyAnimal', 'Stat.Award.LongestShot', 'Stat.Award.Medic'];

/** `guCommText` (CommentaryMan.cpp): the text library prefix of each eCommentType 0..34; the
 * entry is prefix + (choice + 1). */
const COMMENT_PREFIX = [
  'Com.TurnStart.', 'Com.LandDeath.', 'Com.WaterDeath.', 'Com.TeamDeath.',
  ...Array.from({ length: 10 }, (_, i) => `Com.WormKill${i + 1}.`),
  'Com.CrateDrop.', 'Com.WeaponsCrateDrop.', 'Com.CraftCrateDrop.', 'Com.HealthDrop.', 'Com.VehicleDrop.', 'Com.DelayOver.',
  'Com.3MinutesLeft.', 'Com.SuddenDeath.', 'Com.GameDrawn.', 'Com.TeamWin.', 'Com.TeamCrafting.', 'Com.VehicleDestroyed.',
  'Com.ReplayComments.', 'Comm.RopesLeft.', 'Com.BaseballBat.', 'Com.NoUpgradeSlots.', 'Com.NotEnoughAmmo.',
  'Com.MissingIngredients.', 'Com.CraftingInProgress.', 'Com.DismantleInProgress.', 'Com.WeaponDelayed.',
];
/** The first of `guCommText`'s press refusals (Com.NoUpgradeSlots .. Com.WeaponDelayed: crafting
 * and weapon presses the sim refused). */
const COMMENT_REFUSALS = 29;
/** `VehicleGetName` 0xcabdd0 (`VehicleGetTitle`: "Gui.Vehicle.%s"). */
const VEHICLE_NAMES = ['None', 'Tank', 'Helicopter', 'Mech', 'StaticMachineGun', 'StaticMortar', 'StaticFlameThrower', 'StaticSniper', 'StaticDubstepGun', 'RocketLeagueCar', 'DrillBoat'];
/** The crafting ingredients' titles (CogCrate::Collect), CraftingIngredient order. */
const INGREDIENT_TITLES = ['FE.Crafting.Sauce', 'FE.Crafting.Paste', 'FE.Crafting.Seeds', 'FE.Crafting.Tears'];
/** CommentaryMan's display time of a queued comment (seconds): m_fDefaultDisplayTime, which
 * CommentaryMan::Initialize 0xb96030 reads from GameOptions.txt `DefaultCommentaryDisplayTime`. */
const COMMENT_TIME = 1.5;
/** CommentaryMan's queue length. */
const COMMENT_QUEUE = 4;
/** CommentaryMan's priority slot as its constructor (0xb96190) leaves it: `Initialize` clears
 * m_bHasPriorityMessage, but m_bImmediatePriority and the message's display time keep the XOM
 * allocator's 0xCD fill (sim: `CommentaryMan::CommentaryMan`; 0xCDCDCDCD as a float32 is
 * -431602080). So the match's first TransitionOff pops an empty "immediate" message whose delay
 * has passed at once: the original slides an empty panel on and off after the first comment
 * (gdb on match-end-A: lf 726 pops it with delay -431602080). */
const PRIORITY_UNSET: PrioritySlot = { text: '', time: -431602080, has: false, immediate: true };
/** Frames between a frame's logic and its IP2 UI on screen in the original: match-end-A's
 * captures show the commentary slide and the turn timer's digits two frames after the UI logic
 * that set them (the slide at time T of its timeline in frame trigger + T + 2). */
const UI_LAG = 2;
/** HU_TurnArrow_01's "Go" clip length (seconds; played looping). */
const ARROW_LOOP = 0.7916666865348816;
// WormHUDPanel::PanelTransitionState (WormHUDPanel.h:42).
const PANEL_ON_SCREEN = 0, PANEL_MOVING_ON = 1, PANEL_MOVING_OFF = 2, PANEL_OFF_SCREEN = 3, PANEL_JETPACKING = 4;
// WormHUDCurrentArrow's transition states.
const ARROW_NONE = 0, ARROW_MOVING_ON = 1, ARROW_MOVING_OFF = 2;
const f32 = Math.fround;

/** HU_NamePanel's height (the box texture, 3 transparent rows above and below). */
const LABEL_BOX_H = 32;

/** The weapons panel's textures (cIP2_WP_Frame::ConstructWeaponsPanel 0xdba3d0, cIP2_WP_Cell::
 * SetupTextures 0xdca480): RMUI_TYPE_TEXTURE indices of ui_reslist (re/tools/formats/ui.py
 * WP_TEXTURES). */
const WP = { sauce: 0x1d4c, paste: 0x1d42, seeds: 0x1d38, tears: 0x1d2e, cell: 0x1d92, craftCell: 0x1e14, focus: 4, focusCraft: 0xaa };
/** The frame's border textures in weapons and in crafting mode (cIP2_WP_Frame::SetCraftingMode
 * 0xdb8860 swaps in the HU_NewCraftPanel set). */
const FRAME_TEX: Record<string, [number, number]> = {
  TitleBackerLeft: [0x1d9c, 0x1e1e], TitleBackerMiddle: [0x1da6, 0x1e28], TitleBackerRight: [0x1db0, 0x1e32],
  DividerLeft: [0x1dce, 0x1e50], DividerMiddle: [0x1dd8, 0x1e5a], DividerRight: [0x1de2, 0x1e64],
  LeftBorder: [0x1d88, 0x1e0a], RightBorder: [0x1dba, 0x1e3c],
  BottomBorderLeft: [0x1dec, 0x1e6e], BottomBorderMiddle: [0x1df6, 0x1e78], BottomBorderRight: [0x1e00, 0x1e82],
};
/** The crafting menu (cIP2_WP_Crafting: Reset 0xdcd5d0, 266 wide; SetupTextures 0xdcbbb0: the
 * button panels HU_CraftingSidePanel_01..03 for 2, 3 and 4 buttons and
 * HU_CraftingIngredientsPanel_01) and its buttons (cIP2_WP_CraftingButton::Reset 0xdc99e0,
 * 179x52; ConstructButton 0xdc9680: the icon backer HU_CraftingButton_03, 57x52, and the 42x42
 * equip icon 2 left of the button; UpdateLayout 0xdc9880: the cancel icon HU_WPCraft1_01). */
const CRAFT = { menuW: 266, panels: [0x145, 0x146, 0x147], ingredients: 0x140, buttonW: 179, buttonH: 52, backer: 0x24b, backerW: 57, icon: 42, cancelIcon: 0x22 };
const RES = (i: number): number => (9003 << 16) | i;
/** `NUI_Cache::GetPlayerProfileResourceIDFromTeamorUserID` 0xd788e0: a team's picture (the HUD
 * panels' and the hot seat panel's `User.Picture`, the results' `In_Game_Team.DemoAvatar`) by team
 * index: BlankTexture (a transparent texel), HU_PlayerAvatar_01..06. An AI team always gets its
 * index's; a human team its platform avatar, else the same (no team here has an avatar). */
const TEAM_PICTURES = [141, 436, 437, 438, 439, 440, 441];
const teamPicture = (team: number): number => RES(TEAM_PICTURES[team] ?? TEAM_PICTURES[0]);
/** `kEquip_MaxInventory` ("Nothing"): an empty cell. */
const NOTHING = 39;
/** TeamLogic's infinite ammo count. */
const INFINITE_AMMO = 0xff;
/** Fonts (FM_FontManager index; xid = 9007 << 16 | index). */
const FONT = (i: number): number => (9007 << 16) | i;
const F_BLITZ16 = FONT(1001), F_SERIF20 = FONT(1021), F_ORNITONS16 = FONT(1032), F_ORNITONS24 = FONT(1031);

type CommentEntry = { text: string; time: number };
/** CommentaryMan's m_uPriorityMessage with m_bHasPriorityMessage and m_bImmediatePriority: one
 * message, overwritten by each AddPriorityComment. */
type PrioritySlot = { text: string; time: number; has: boolean; immediate: boolean };
type Cell = { item: Item; equip: number; slot: number | null };
/** A crafting menu button (cIP2_WP_CraftingButton): its item, equip icon, `m_EquipID` (39 after
 * Reset) and kind (`m_bIsDismantleButton`, `m_bIsCancelButton`). */
type CraftButton = { item: Item; icon: Item; equip: number; dismantle: boolean; cancel: boolean };
/** `EquipIsValid` 0xc33cc0 (no reward equips here): 0..133 but kEquip_MaxInventory. */
const equipValid = (e: number): boolean => e !== NOTHING && e >= 0 && e <= 133;

/** `Dcol` (cIP2_2DP::SetAllColours): 0xBBGGRRAA. */
const dcol = (v: number): Rgba => [((v >>> 8) & 0xff) / 255, ((v >>> 16) & 0xff) / 255, ((v >>> 24) & 0xff) / 255, (v & 0xff) / 255];
const flat = (colour: number): Rgba => {
  const c = FLAT_COLOURS[Math.min(Math.max(colour, 0), 6)];
  return [c[0] / 255, c[1] / 255, c[2] / 255, 1];
};
const setColour = (it: Item, c: Rgba): void => { it.colours = [c, [...c] as Rgba, [...c] as Rgba, [...c] as Rgba]; };
const pad2 = (n: number): string => String(n).padStart(2, '0');

export class Hud {
  root: HTMLElement;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private bannerEl: HTMLElement;
  private ui: UiData | null = null;
  private scene: Scene | null = null;
  private painter: Ip2Painter | null = null;
  private hud: HudView | null = null;
  private recipes: Recipe[] = [];
  private panelInfo: PanelInfo | null = null;
  /** The HUD aim and targeting widgets' client state (game/widgets.ts). */
  private widgets = new AimWidgets();
  /** The scenes' 3-D items (cIP2_2DXom captures). */
  private xom: UiXom | null = null;
  /** XOM items attached to a node of another (`m_ParentNodeName` on `m_ParentXOMID`), by parent. */
  private xomAttached = new Map<Item, { xdesc: string; node: string }[]>();
  teams: TeamInfo[] = [];
  /** An online match (`NetSetup::IsLobbyAvailable`: the room's lobby, not the offline replay). */
  private online = false;
  /** Per team the user line of the NDS primers: its user's chopped name in an online match, null
   * offline and for a team without a user (an AI team). */
  private users: (string | null)[] = [];
  /** A pick of the cell of `equip` (`craftingSlot`: a crafted weapon's slot). */
  onPick: ((equip: number, craftingSlot: number | null) => void) | null = null;
  /** A crafting button press (sim CraftingButton: action 0 craft, 1 dismantle, 2 cancel). */
  onCraft: ((team: number, action: number, equip: number) => void) | null = null;
  /** The panel opened for `team` (`panelTeam`): the crafting menu shows that team's CraftingMan
   * answers, which the worker computes from then on. */
  onPanelTeam: ((team: number) => void) | null = null;
  /** The results screen's Continue (ClientMsg::Continue: CanExitMatch at the host's press). */
  onContinue: (() => void) | null = null;
  /** Tests: called for every HUD step at its tick, once the step's inputs are taken and before
   * the state machine runs it (actions at exact ticks, as the captures set them from gdb). */
  onStep: ((tick: number) => void) | null = null;

  // UICallback_MainGame_OnIdle state (gUICB_MainGame_DATA)
  private lastTick = -1;
  /** Observed steps not run yet, oldest first (setViews). */
  private steps: StepInput[] = [];
  private state = START_INIT;
  private lastTeam = -1;
  private time = 0;
  /** UpdateTurnTimer's hot-seat and turn-time ticks. */
  private readonly timerSounds = new TimerSounds(path => audio.playUi(path));
  // health panels
  private locations: { x: number; y: number }[] = [];
  private endY: number[] = [];
  private barEnd: number[] = [];
  private highest = 0;
  private panelsState = ON;
  private panelsX = 0;
  private panelsEndX = 0;
  // commentary (CommentaryMan queue + UpdateCommentaryPanel)
  private nextSeq = 0;
  private queue: CommentEntry[] = [];
  private prio: PrioritySlot = { ...PRIORITY_UNSET };
  private commentState = OFF;
  private commentDelay = 0;
  private commentStart = 0;
  // worm labels, damage numbers and the arrow (WormHUDMan, WormHUDPanel::UpdateTransitionState):
  // game objects, run with the frame's tick (no IP2 lag)
  private wormSteps: StepInput[] = [];
  private wormTick = -1;
  private wormHud: HudView | null = null;
  /** WormHUDPanel by worm index (only worms that were the current worm can leave k_OnScreen). */
  private panels = new Map<number, WormPanel>();
  /** WormHUDCurrentArrow: `m_bIsActive`, `m_eTransitionState`, `m_vOffset.y`, `m_vPosition`
   * (the worm's graphical position at the last SetPosition: every tick UpdateTransitionState
   * keeps the arrow on, the WMH5 view's `enter.gx`/`gy`) and the "Go" clip's time since its
   * PlayAnim. */
  private arrow = { active: false, state: ARROW_NONE, offset: 0, x: 0, y: 0, time: 0 };
  /** The last PanelVehicle run applied (hudview.ts HudEnter.runs). */
  private enterRuns = -1;
  /** `CameraMan::GetCameraBounds`' top right y (the round bounds): the arrow drops in from 100
   * above it, the panels and the arrow move off towards it. */
  private camTop = 0;
  // weapons panel (cIP2_WP_Frame)
  private frame: Item | null = null;
  private titleBox: Item | null = null;
  private cells: Cell[] = [];
  private craftCells: Cell[] = [];
  private ingredients: Item[] = [];
  private equipTab: Item | null = null;
  private craftTab: Item | null = null;
  private wpState = OFF;
  /** cIP2_WP_Frame::m_bCraftingMode. */
  private crafting = false;
  /** The panel's focused cell or crafting button (cIP2_FocusSystem; a mouse-over focuses). */
  private focus: Cell | null = null;
  private focusButton: CraftButton | null = null;
  private wpTeam = -1;
  /** The equip the frame's title shows (cIP2_WP_Frame::SetEquipTitle from a FocusGain; 39: " "). */
  private titleEquip = NOTHING;
  /** The frame's border textures by name (FRAME_TEX). */
  private frameTex = new Map<string, Item>();
  // the crafting menu (cIP2_WP_Crafting)
  private menu: Item | null = null;
  private buttons: CraftButton[] = [];
  private sidePanel: Item | null = null;
  private ingredientsPanel: Item | null = null;
  private ingredientLines: Item[] = [];
  /** m_ParentEquip, m_OriginialEquip, m_VariationID, m_bIsDismantle. */
  private parentEquip = NOTHING;
  private originalEquip = NOTHING;
  private variation = NOTHING;
  private variationDismantle = false;
  /** m_bIsCrafting: the panel team crafts or dismantles (the cancel layout). */
  private menuCrafting = false;
  /** m_pCurrentButtonPanel: a TEXTURE index, 0 for the null pointer a layout sets before
   * SetupTextures ran (`menuTextures`: the first Render, which forces the 4-button panel). */
  private buttonPanel = 0;
  private menuTextures = false;
  /** The panel team's CraftingMan answers at the HUD's tick (the newest due observed step's). */
  private craft: CraftChecks | null = null;
  /** The results' parade, ResultsParade01..03 (tests; null: UICallback_RoundResults_OnCreate's
   * `SSRUnsafeRand() % 3 + 1` on the graphical generator, seeded with the hi-res clock: a
   * different parade per machine and run). */
  parade: number | null = null;
  /** gUICB_MainGame_DATA.m_fPreviousWindSpeed: floor(10 x wind) of the playing wind clip. */
  private prevWind = 1000;
  // the round results (wmd_roundresults, UICallback_RoundResults_*)
  private results: Scene | null = null;
  /** gUICB_MainGame_DATA.m_bHasShownResults: InEndOfRound sent MNU_Quit its "NormalExit". */
  private shownResults = false;
  /** The HUD menu took its results exit (Fade_Poly's "Fade To Frontend" EXIT, flow link 1): the
   * flow creates the round results menu at the next UI step. */
  private resultsPending = false;
  /** The results menu was created (its ON_CREATE timelines run from then); it replaces the HUD. */
  private resultsStarted = false;
  private resultsShown = false;
  private continued = false;

  constructor(parent: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'hud';
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'hud-canvas';
    this.root.appendChild(this.canvas);
    this.bannerEl = document.createElement('div');
    this.bannerEl.className = 'hud-banner';
    this.root.appendChild(this.bannerEl);
    parent.appendChild(this.root);
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('hud: no 2d context');
    this.ctx = ctx;
    this.canvas.addEventListener('mousemove', e => this.pointer(e, false));
    this.canvas.addEventListener('mousedown', e => {
      e.preventDefault();
      if (e.button === 2) this.togglePanel(false);
      else this.pointer(e, true);
    });
    this.canvas.addEventListener('contextmenu', e => e.preventDefault());
    window.addEventListener('keydown', e => { if (e.key === 'Enter' && this.resultsShown) this.continueResults(); });
    void this.load();
  }

  private async load(): Promise<void> {
    const [ui, scene, results] = await Promise.all([UiData.load(), Scene.load('wmd_hud'), Scene.load('wmd_roundresults')]);
    this.ui = ui;
    this.scene = scene;
    this.painter = new Ip2Painter(this.ctx, ui);
    this.xom = new UiXom();
    this.painter.xom = (it, box) => this.drawXom(it, box);
    this.setupScene(scene);
    // The HUD menu's flow exit 1 (Fade_Poly's "Fade To Frontend" after the logo wipe "Start")
    // leads to the round results (UICallback_Can_Enter_Results lets it through offline).
    scene.onExit = (it, output) => { if (it.name === 'Fade_Poly' && output === 1) this.resultsPending = true; };
    scene.xomFinished = it => this.xomFinished(it);
    results.xomFinished = it => this.xomFinished(it);
    for (const n of ['MNU_TrainingResults', 'MNU_CampaignResults', 'MNU_ChallengeResults', 'MNU_Saving', 'MNU_RankResultsGroup', 'MNU_OnlineDeathmatchLegend', 'MNU_CurrentMission']) {
      const it = results.find(n);
      if (it) it.active = false;
    }
    this.results = results;
    for (const sc of [scene, results]) {
      for (const it of sc.items) {
        const x = it.sections.xom as XomSection | undefined;
        const parent = x?.m_ParentXOMID && x.m_ParentNodeName ? sc.items.find(p => p.id === x.m_ParentXOMID) : undefined;
        const xdesc = parent && ui.mesh(it.resId);
        if (parent && xdesc) this.xomAttached.set(parent, [...(this.xomAttached.get(parent) ?? []), { xdesc, node: x!.m_ParentNodeName! }]);
      }
    }
    // Every sprite the HUD and the results may draw is decoded before the match (UiData.texture:
    // a sprite first requested in a frame is missing until the decoder worker made it): the
    // scenes' textures, their widgets' style textures, the worm labels' name panel, the team
    // pictures and the award icons.
    const items = [...scene.items, ...results.items];
    await ui.preload([
      ...items.filter(i => i.type === T_TEX && i.resId).map(i => i.resId), ...items.flatMap(i => widgetTextures(ui, i)),
      'HU_NamePanel_01.png', 'HU_NamePanel_02.png', 'HU_NamePanel_03.png', ...TEAM_PICTURES.map(RES), ...AWARD_ICONS.map(RES),
    ]);
  }

  /** What UICallback_MainGame_OnCreate 0xea9090 and the first frames leave hidden or set. */
  private setupScene(s: Scene): void {
    for (const n of ['MNU_Debug', 'MNU_Pause', 'MNU_Hotseat', 'MNU_[pc]_GameChat', 'MNU_[pc]_ChatIndicator', 'MNU_Resynching',
      'MNU_TrainingTime', 'MNU_CampaignIntro', 'MNU_TrainingIntro', 'MNU_ChallengeIntro', 'FullscreenFade', 'Fade_Poly',
      ...[1, 2, 3, 4, 5, 6].map(i => `MNU_PlayerVoice_0${i}`)]) {
      const it = s.find(n);
      if (it) it.active = false;
    }
    const players = s.get('MNU_Players');
    this.panelsX = this.panelsEndX = players.x;
    for (let i = 0; i < 6; i++) {
      const p = s.get(`MNU_Player_0${i + 1}`);
      this.locations.push({ x: p.x, y: p.y });
      this.endY.push(p.y);
      this.barEnd.push(1);
      // Ranked-match items (MNU_RankingActivator / TEX_RankIcon): not in an unranked match.
      for (const c of p.children) if (c.name === 'MNU_RankingActivator' || c.name.startsWith('TEX_RankIcon')) c.active = false;
      // The team's ranked rewards: OnCreate sets the health bar effect (RewardSelection::
      // GetHealthBarEffectXOM, HU_NRankings_01 with GetHealthBarAnimName) and the bar background
      // (GetHealthBGTexture) of a team that has them and SetActive(0)s both otherwise. No team has
      // rewards here.
      for (const n of [`XOM_HealthbarEffect_0${i + 1}`, `TEX_RankBackgroundReward_0${i + 1}`]) {
        const it = s.find(n);
        if (it) it.active = false;
      }
    }
    // The commentary panel waits off screen: the original's authored place (y 456) lies above
    // the screen; here it rests inactive at its SlideOn's start (y 124, partly on screen).
    const commentary = s.get('MNU_Commentary_01');
    commentary.y = 124;
    commentary.active = false;
  }

  /** The sim's HUD views since the previous frame (hudview.ts): every fully observed step's
   * inputs, run tick by tick in the next update, and the recipe and panel views. */
  setViews(huds: HudStep[], recipes?: Uint8Array, panel?: Uint8Array): void {
    for (const st of huds) {
      const s: StepInput = {
        h: decodeHud(st.hud), turnFlags: st.turnFlags, currentWorm: st.currentWorm, currentTeam: st.currentTeam, health: st.health, maxHealth: st.maxHealth, wind: st.wind, phase: st.phase,
        craft: st.craft ? decodeCraft(st.craft) : undefined, restored: !!st.restored,
      };
      this.steps.push(s);
      this.wormSteps.push(s);
    }
    if (recipes) this.recipes = decodeRecipes(recipes);
    if (panel) {
      this.panelInfo = decodePanel(panel);
      this.frame = null;
    }
  }

  banner(text: string): void {
    this.bannerEl.textContent = text;
    this.bannerEl.style.display = text ? 'block' : 'none';
  }

  panelOpen(): boolean {
    return this.wpState === ON || this.wpState === TRANS_ON;
  }

  /** The round results are shown (`NetGame::IsLocalMenuOpen(kMenu_RoundResults)`). */
  resultsOpen(): boolean {
    return this.resultsShown;
  }

  /** Tests: the canvas rectangle of the open panel's cell of `equip` (null while it slides). */
  cellRect(equip: number): { x: number; y: number; w: number; h: number } | null {
    const c = this.cells.find(x => x.equip === equip);
    if (!c || this.wpState !== ON || !this.painter || !c.item.pActive) return null;
    const [x, y, w, h] = this.painter.boxOf(c.item);
    return { x, y, w, h };
  }

  /** cIP2_WP_Frame::Open / Close for the panel's team (UICBInterface_ALL_SCENE::
   * g_uTeamOpenedWeaponPanel, `panelTeam`); nothing opens without one. The focus system focuses
   * the first cell (weaponpanel.png). */
  togglePanel(show?: boolean): void {
    const open = show ?? !this.panelOpen();
    if (open === this.panelOpen() || !this.frame) return;
    if (open) {
      const team = this.panelTeam();
      if (team < 0) return;
      this.frame.parent!.active = true;
      this.frame.active = true;
      this.wpState = TRANS_ON;
      this.wpTeam = team;
      this.onPanelTeam?.(team);
      this.setFocus(this.cells[0] ?? null, null);
    } else this.wpState = TRANS_OFF;
    this.canvas.style.pointerEvents = open ? 'auto' : 'none';
  }

  /** The team the weapons panel opens for. `UICBInterface_ALL_SCENE::UpdateWeaponsPanel` 0xea5730
   * opens it for the team of the player who pressed (`UserMan::MapGuiToTeam(g_uLastGUIUser)`,
   * kept as `g_uTeamOpenedWeaponPanel`) when that team is alive and is the current team, or the
   * current team is an AI, or the game is a network game (every match of the room's lobby). Its
   * picks act only while it is the current team (`cIP2_WP_Cell::ControllerAccept` 0xdca1e0) and
   * its crafting buttons press for it (`cIP2_WP_CraftingButton::ControllerAccept` 0xdc9bc0): a
   * player crafts for its own team in any turn, an AI's included, never for another team. A page
   * plays all its teams with one keyboard and mouse: the current team when it is one of them,
   * else the first of them alive. Offline (the replay) every team is the page's: the current
   * one. -1: none (a spectator, or every team of the page is out). */
  private panelTeam(): number {
    if (!this.online) return this.lastTeam;
    const ours = (t: number): boolean => !!this.teams[t]?.mine && (this.hud?.teams[t]?.alive ?? true);
    if (this.lastTeam >= 0 && ours(this.lastTeam)) return this.lastTeam;
    return this.teams.findIndex((_, t) => ours(t));
  }

  update(v: FrameView, r: Renderer): void {
    this.resize();
    const s = this.scene, ui = this.ui, p = this.painter;
    // The IP2 UI runs to UI_LAG ticks before the frame (steps of a newer sim than the frame's
    // run at once: they reset the HUD). The WormHUD objects run to the frame's tick.
    this.camTop = r.cameraMan.bounds().tr.y;
    this.stepWorms(Infinity, r.cameraMan);
    const due: StepInput[] = [], later: StepInput[] = [];
    for (const st of this.steps) (st.h.tick <= v.tick - UI_LAG || st.h.tick > v.tick ? due : later).push(st);
    const latest = due.at(-1)?.h ?? this.hud;
    if (!s || !ui || !p || !latest) return;
    this.steps = later;
    if (this.panelInfo && !this.frame) this.buildWeaponsPanel(s, this.panelInfo, latest);
    // Each observed step in tick order (a fast-forward or a catch-up posts every tick's HUD inputs
    // too, so a long one arrives here at once); a step stands for the ticks since the previous
    // one. The scene's timelines advance with every tick
    // (cIP2_2DSystem::ProcessLogic after OnIdle), so the state machine sees their progress; so do
    // the round results' once the flow created them.
    for (const st of due) {
      if (st.h.tick < this.lastTick || st.restored) this.reset();
      const first = this.lastTick < 0;
      const n = first ? 1 : Math.min(600, st.h.tick - this.lastTick);
      this.lastTick = st.h.tick;
      this.hud = st.h;
      if (st.craft !== undefined) this.craft = st.craft;
      if (st.currentTeam !== this.lastTeam) {
        this.lastTeam = st.currentTeam;
        this.togglePanel(false);
      }
      // Comments made before this HUD's first step (a late join, a replay's fast-forward) were
      // shown already, and the first of them has used up the constructor's priority flag.
      if (first) {
        this.nextSeq = st.h.nextComment;
        if (st.h.nextComment > 0) this.prio.immediate = false;
      }
      this.takeComments(st.h, v);
      this.onStep?.(st.h.tick);
      if (st.restored) this.sync(st);
      else for (let f = 0; f < n; f++) {
        if (this.resultsPending) this.createResults();
        this.step(st, st.h.tick - (n - 1 - f));
        s.animate(1);
        if (this.resultsStarted) this.results!.animate(1);
      }
    }
    this.timerTick(v.tick);
    const h = this.hud ?? latest;
    this.refresh(v, h, ui);
    this.refreshWeaponsPanel(h);
    s.place();
    const ctx = this.ctx;
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    p.viewport(this.canvas.width, this.canvas.height);
    // The round results replace the HUD once the HUD's exit created them (MainGame_OnIdle's
    // InEndOfRound: MNU_Quit's NormalExit, the logo wipe, the flow's results link).
    this.resultsShown = this.resultsStarted;
    this.canvas.style.pointerEvents = this.resultsShown || this.panelOpen() ? 'auto' : 'none';
    if (this.resultsShown) {
      r.hudMesh('turnArrow', 'HU_TurnArrow_01', 0, 0, false, 'Go', 0, [1, 1, 1]);
      this.drawResults(h, p);
      return;
    }
    this.continued = false;
    this.drawLabels(v, this.wormHud ?? h, r, p);
    p.draw(s);
    this.drawWeaponsPanelText(h, p);
  }

  /** The match's teams; `online`: a match of the room's lobby (the user lines of `users`). */
  setTeams(teams: TeamInfo[], online: boolean): void {
    this.teams = teams;
    this.online = online;
    this.users = teams.map(t => (online && t.user !== null ? primerUserName(t.user) : null));
  }

  private continueResults(): void {
    if (this.continued) return;
    this.continued = true;
    this.onContinue?.();
  }

  /** The flow's round results menu (`cIP2_RFlowScene::ProcessLogic` creating wmd_roundresults,
   * UICallback_RoundResults_OnCreate 0xf0f570), at the UI step after the HUD's exit: its XOM
   * items restart their default animations and its ON_CREATE timelines run from this step
   * (XOM_Banner plays "Start", then "Loop" from frame 960; Fade_Poly's logo wipe "End";
   * MNU_DeathmatchResults fades in after 150 frames). match-end-A: the HUD sees the round's
   * ShowRoundResults at tick 5217 (Goodbye1, lf 3348), its exit takes the logo wipe's 0.5 s
   * "Start", and the results start where the original's OnCreate draws its parade (frame 5252). */
  private createResults(): void {
    this.resultsPending = false;
    const sc = this.results;
    if (!sc) return;
    this.resultsStarted = true;
    for (const it of sc.items) {
      it.running = [];
      if (it.type === T_XOM) xomDefault(it);
    }
    // XOM_Parade_01 plays "ResultsParade%02u.xDesc" (MESH 19..21) with n = SSRUnsafeRand() % 3 + 1
    // (0xf0f9b9), "Go" looping.
    const parade = sc.find('XOM_Parade_01'), n = this.parade ?? 1 + Math.floor(Math.random() * 3);
    if (parade) parade.resId = (parade.resId & ~0xffff) | (18 + n);
    // NDSPRIMER_Winner_Animation 0xec6ed0 / Winner_Hat 0xec6b70: the winner's worm and hat get a
    // mesh only when the team's MatchSpec VictoryDanceAnim / HeadAttachmentGID names an
    // RGVictoryDance / RGWormAttachment entry; the match setups leave both empty (matchsetup
    // spec.rs, as the original's captures: no worm over the banner).
    for (const n of ['XOM_TeamWorm_01', 'XOM_Hat_01']) { const it = sc.find(n); if (it) it.active = false; }
    for (const it of sc.items) sc.trigger(it, MSG_ON_CREATE);
    // OnCreate's music and parade sound, then the first OnIdle's UpdateFanfare: the leading team
    // first in BaseRoundStats' ranking, a draw without a victorious alliance.
    audio.resultsMusic(n);
    const h = this.hud;
    if (h) {
      const order = h.teams.map((t, i) => ({ t, i })).sort((a, b) => (a.t.round.rank >>> 0) - (b.t.round.rank >>> 0) || a.i - b.i);
      const leading = order[0]?.i ?? -1, mine = this.teams.findIndex(t => t.mine);
      const f = fanfareSounds(leading, h.victorious === 0xffffffff, mine >= 0 ? mine : leading);
      audio.resultsFanfare(f.fanfare, f.crowd);
    }
  }

  /** UpdateTurnTimer's sounds at frame `tick`: OnIdle calls it before its state switch, with the
   * logic of the tick before the frame, one frame ahead of the scene (UI_LAG): the state is the
   * one the steps up to `tick - 2` left, the turn's seconds, teleport and hot-seat flags are step
   * `tick - 1`'s (timer updates taken as allowed). The audio trace measures this timing (hbs
   * HotseatTick, match-teardown TurnTimeLowTick: docs/platform/UiFidelity.md §3). */
  private timerTick(tick: number): void {
    const st = this.steps.filter(x => x.h.tick <= tick - 1).at(-1);
    const h = st?.h ?? this.hud;
    if (!h) return;
    const hotSeat = ((st?.turnFlags ?? 0) & 2) !== 0, teleport = (h.flow & FLOW_TELEPORTING) !== 0, secs = h.turnSecs;
    const flash = !(secs > 5 || (this.state !== IN_TURN && !(teleport && !hotSeat)) || secs < 0.1);
    this.timerSounds.update(Math.fround(tick * (1 / 60)), flash, true);
  }

  /** The menus leave the round's end (a new round): the HUD's exit can run again. */
  private dropResults(): void {
    if (this.resultsStarted) audio.resultsMusicEnd();
    this.resultsStarted = this.resultsPending = this.shownResults = false;
    const s = this.scene;
    if (!s) return;
    for (const n of ['Fade_Poly', 'MNU_Quit']) {
      const it = s.find(n);
      if (it) it.running = [];
    }
    const fade = s.find('Fade_Poly');
    if (fade) fade.active = false;
  }

  /** `cIP2_2DXom::HasCurrentAnimationFinished` 0xf6dd90: false while looping; true without a
   * current animation; else the animation's time reached the clip's duration (XMeshInstance::
   * GetAnimDuration; false while the mesh is unknown). */
  private xomFinished(it: Item): boolean {
    if (it.xomLoop) return false;
    if (it.xomAnim === null) return true;
    const xdesc = this.ui?.mesh(it.resId);
    const d = xdesc ? clipDuration(xdesc, it.xomAnim) : null;
    return d !== null && it.xomTime >= d - 1e-6;
  }

  /** The deathmatch results (MNU_DeathmatchResults of wmd_roundresults; the In_Game_Team NDS
   * primers): the rows in BaseRoundStats rank order (Results_List), the position, the user name
   * (`NDSPRIMER_Gamer_Name`: online the team's user, " " without one; offline the original leaves
   * its placeholder), the team name, the round's kills, deaths and damage dealt, the team wins,
   * the award (StatsMan::GetTeamAward's icon and title),
   * the team's picture (`teamPicture`); the legend "Continue [Enter]". The 3-D items
   * (XOM_Sky, XOM_Banner, the generals, the team worm and its hat, the spectators and the
   * parade) are drawn in the 2-D layer order through `drawXom`. */
  private drawResults(h: HudView, p: Ip2Painter): void {
    const sc = this.results;
    if (!sc) return;
    const ui = this.ui!;
    const order = h.teams.map((t, i) => ({ t, i })).sort((a, b) => (a.t.round.rank >>> 0) - (b.t.round.rank >>> 0) || a.i - b.i);
    for (let row = 1; row <= 6; row++) {
      const m = sc.find(`MNU_RoundResults_0${row}`);
      if (!m) continue;
      const e = order[row - 1];
      m.active = !!e;
      if (!e) continue;
      const get = (name: string): Item | null => sc.under(m, `${name}_0${row}`);
      const set = (name: string, text: string): void => { const it = get(name); if (it) it.text = text; };
      set('TXT_Position', `${row}.`);
      set('TXT_TeamName', this.teams[e.i]?.name ?? `Team ${e.i + 1}`);
      if (this.online) set('TXT_Username', this.users[e.i] ?? ' ');
      set('TXT_Kills', String(e.t.round.kills));
      set('TXT_Deaths', String(e.t.round.deaths));
      set('TXT_Damage', String(e.t.round.damage));
      set('TXT_Rounds', String(this.wins(h, e.i)));
      const col = flat(this.teams[e.i]?.colour ?? e.i);
      for (const n of ['TXT_Username', 'TXT_TeamName', 'TXT_Position']) { const it = get(n); if (it) setColour(it, col); }
      const offline = get('MNU_OfflineDeathmatch');
      const pic = offline && sc.under(offline, `TEX_ProfilePic_0${row}`);
      if (pic) { pic.active = true; pic.tex = teamPicture(e.i); }
      const award = AWARD_TITLES[e.t.award];
      const icon = get('TEX_Award'), title = get('TXT_AwardTitle');
      if (icon) { icon.active = !!award; icon.tex = RES(AWARD_ICONS[e.t.award] ?? 0); }
      if (title) { title.active = !!award; title.text = award ? (ui.text(award) ?? '') : ''; }
      for (const c of m.children) if (c.name.startsWith('MNU_Online') || c.name.startsWith('MNU_Chat')) c.active = false;
    }
    for (const it of sc.items) {
      if (it.name !== 'TXT_Legend') continue;
      const tok = (it.sections.txt as Record<string, unknown> | undefined)?.m_LocaliseToken;
      const pc = tok === 'FE.RoundResults.LegendPC02';
      for (let q: Item | null = it; q; q = q.parent) if (pc || q === it) q.active = pc;
    }
    const res = sc.find('MNU_DeathmatchResults');
    if (res) res.active = true;
    sc.place();
    p.draw(sc);
  }

  private resize(): void {
    const b = this.root.getBoundingClientRect();
    const w = Math.max(1, Math.round(b.width)), hh = Math.max(1, Math.round(b.height));
    if (this.canvas.width !== w || this.canvas.height !== hh) {
      this.canvas.width = w;
      this.canvas.height = hh;
    }
  }

  private reset(): void {
    this.lastTick = -1;
    this.state = START_INIT;
    this.nextSeq = 0;
    this.queue = [];
    this.prio = { ...PRIORITY_UNSET };
    this.commentState = OFF;
    this.highest = 0;
    this.prevWind = 1000;
    this.dropResults();
    if (this.scene) {
      const commentary = this.scene.get('MNU_Commentary_01');
      commentary.y = 124;
      commentary.active = false;
    }
  }

  // ---- one UI frame (60 Hz): UICallback_MainGame_OnIdle 0xea84c0 ----

  private step(st: StepInput, tick: number): void {
    const h = st.h;
    // XomGetSystemTimeSecs: the presented frame / 60 as a float32 (the harness's fixed clock).
    this.time = Math.fround(tick * (1 / 60));
    const inTurn = (st.turnFlags & 1) !== 0, hotSeat = (st.turnFlags & 2) !== 0, roundDone = (h.flow & FLOW_ROUND_DONE) !== 0;
    // LAB_00ea863c: NUI_Cache::m_bShowRoundResults (set with Goodbye1 by BaseRoundLogic::
    // Update_WaitSaveGame) moves any other state to StartEndOfRound.
    const showResults = isShowResults(st.phase);
    if (!showResults && (this.shownResults || this.resultsStarted)) this.dropResults();
    if (showResults && this.state !== START_END_ROUND && this.state !== IN_END_ROUND) this.state = START_END_ROUND;
    switch (this.state) {
      case START_INIT:
        this.showHotSeat(false);
        this.state = INIT;
        break;
      case INIT:
        // RestartFinished: the first frame of the round
        this.updateBars(st.health);
        this.sortPanels(st.health, h);
        this.state = IN_UPDATE_HEALTH;
        break;
      case START_IN_TURN:
        this.showHotSeat(false);
        this.timerSounds.inTurnStart();
        this.state = IN_TURN;
        break;
      case IN_TURN:
        this.updateInTurn(h);
        this.updateWindArrows(st.wind);
        if (hotSeat) this.state = START_HOT_SEAT;
        else if (h.flow & FLOW_RETREAT_REQUESTED) this.state = START_RETREAT;
        else if (h.flow & FLOW_TURN_DONE) this.state = START_END_TURN;
        else if (roundDone) this.state = START_END_ROUND;
        break;
      case START_RETREAT:
        this.state = IN_RETREAT;
        break;
      case START_END_TURN:
        this.state = IN_END_TURN;
        break;
      case IN_RETREAT: case IN_END_TURN:
        if ((h.flow & FLOW_DAMAGE_APPLIED) !== 0 && !inTurn) this.state = START_UPDATE_HEALTH;
        else if (hotSeat) this.state = START_HOT_SEAT;
        else if (roundDone) this.state = START_END_ROUND;
        break;
      case START_UPDATE_HEALTH:
        this.showPanels(true);
        this.updateBars(st.health);
        this.sortPanels(st.health, h);
        this.state = IN_UPDATE_HEALTH;
        break;
      case IN_UPDATE_HEALTH:
        if (hotSeat) this.state = START_HOT_SEAT;
        else if (inTurn) this.state = START_IN_TURN;
        else if (roundDone) this.state = START_END_ROUND;
        break;
      case START_HOT_SEAT:
        // `UICallback_MainGame_OnIdle` 0xea84c0 hides the panel here only in a local offline game
        // (its network branch keeps it: `bVar2` false); the team change closes it already.
        if (!this.online) this.togglePanel(false);
        if (!(h.flow & FLOW_END_OF_HOTSEAT)) this.showHotSeat(true);
        // OnIdle's clock: the frame after the step's tick (see update's UpdateTurnTimer).
        this.timerSounds.hotSeatStart(Math.fround((tick + 1) * (1 / 60)));
        this.state = IN_HOT_SEAT;
        this.flashCurrentTeam(st.currentTeam);
        this.showPanels(true);
        this.updateBars(st.health);
        this.sortPanels(st.health, h);
        break;
      case IN_HOT_SEAT:
        this.updateWindArrows(st.wind);
        if (h.flow & FLOW_END_OF_HOTSEAT) this.showHotSeat(false);
        if (inTurn) this.state = START_IN_TURN;
        else if (roundDone) this.state = START_END_ROUND;
        break;
      case START_END_ROUND:
        this.state = IN_END_ROUND;
        break;
      case IN_END_ROUND: {
        // m_bShowRoundResults: MNU_Quit's ON_USER_1 "NormalExit" (Fade_Poly on, its "Fade To
        // Frontend" a frame later) once, unless the screen transition's ON_USER_1 still runs.
        const s = this.scene;
        if (showResults && !this.shownResults && s) {
          const fade = s.find('Fade_Poly'), quit = s.find('MNU_Quit');
          if (!fade || !s.isRunning(fade, MSG_USER_1)) {
            this.shownResults = true;
            if (quit) s.trigger(quit, MSG_USER_1);
          }
        }
        break;
      }
      default:
        break;
    }
    this.animatePanels();
    this.updateCommentary();
    this.stepWeaponsPanel();
  }

  /** The first step after a snapshot restore (a late join, a reconnect or a resync: netcode.md
   * §8; the base is the latest barrier, a turn's end): the HUD never saw the round before it, so
   * what the original's UI built up over the round is taken from the sim's state, not started
   * over at InitialisingGame (docs/platform/UiFidelity.md §2 "A HUD without the round's
   * history"):
   * - the game state from the flags: the round's end, the hot seat, in a turn, after the
   *   damage of a finished turn (InUpdateHealth, which StartUpdatingHealth enters once the
   *   damage is applied out of the turn), a retreat (the retreat request), a turn's end (the
   *   turn ender's Done), else between turns;
   * - m_fHighestHealth, the UI's running maximum over its updates of the living teams' health:
   *   the highest BaseRoundStats::GetMaximumTeamHealth (each team's running maximum; every team
   *   is at its maximum at the round's first update); the bars and their order at once;
   * - the panels hidden while the turn's last worm input is less than 5 s old (UpdateInTurn's
   *   rule, which only runs in the turn: a finished turn keeps its last inactive time);
   * - the hot seat panel (unless its end was requested) and the current team's flashing bar;
   * - the wind clip of the turn's wind as if started with the turn's hot seat (held at its end
   *   once the turn is over). */
  private sync(st: StepInput): void {
    const h = st.h, s = this.scene!;
    const inTurn = (st.turnFlags & 1) !== 0, hotSeat = (st.turnFlags & 2) !== 0;
    this.time = Math.fround(h.tick * (1 / 60));
    this.state = h.flow & FLOW_ROUND_DONE ? IN_END_ROUND : hotSeat ? IN_HOT_SEAT
      : inTurn && !(h.flow & (FLOW_RETREAT_REQUESTED | FLOW_TURN_DONE)) ? IN_TURN
      : !inTurn && h.flow & FLOW_DAMAGE_APPLIED ? IN_UPDATE_HEALTH
      : h.flow & FLOW_RETREAT_REQUESTED ? IN_RETREAT : h.flow & FLOW_TURN_DONE ? IN_END_TURN : IN_UPDATE_HEALTH;
    if (isShowResults(st.phase)) {
      // A HUD that starts at the results (a late join): the menu the flow left there, no wipe.
      this.state = IN_END_ROUND;
      this.shownResults = true;
      this.createResults();
      this.results?.animate(1);
    }
    this.highest = Math.max(0, ...st.maxHealth);
    this.updateBars(st.health);
    this.sortPanels(st.health, h);
    for (let i = 0; i < 6; i++) {
      const p = s.get(`MNU_Player_0${i + 1}`);
      p.y = this.endY[i];
      const bar = s.under(p, `MNU_HealthBar_0${i + 1}`);
      if (bar) bar.sx = this.barEnd[i];
    }
    const players = s.get('MNU_Players');
    const hidden = (this.state === IN_TURN || this.state === IN_RETREAT || this.state === IN_END_TURN) && this.recentInput(st, 5);
    this.panelsEndX = players.x = hidden ? this.panelsX - 650 : this.panelsX;
    this.panelsState = hidden ? OFF : ON;
    players.active = !hidden;
    this.showHotSeat(this.state === IN_HOT_SEAT && !(h.flow & FLOW_END_OF_HOTSEAT));
    if (this.state === IN_HOT_SEAT) this.flashCurrentTeam(st.currentTeam);
    this.updateWindArrows(st.wind);
    const wind = s.find('XOM_Wind');
    if (wind) wind.xomTime = this.state === IN_HOT_SEAT ? h.hotSeatTime - h.turnSecs : this.state === IN_TURN ? h.hotSeatTime + h.turnTime - h.turnSecs : Infinity;
  }

  /** Whether the turn's last worm input is less than `secs` old, from the sim's state alone: the
   * turn's InactiveTime (0 at the turn's start and on input, + kLogicUpdate per tick in the turn
   * only) below `secs` and, in the turn, below its elapsed time (MatchSpec turn time -
   * BaseTurnStats::GetSeconds; equal while there was no input). */
  private recentInput(st: StepInput, secs: number): boolean {
    const h = st.h;
    return h.inactive < secs && ((st.turnFlags & 1) === 0 || h.inactive + 1 / 30 < h.turnTime - h.turnSecs);
  }

  /** The renderer's hook after the camera step of `tick` (Renderer.onCameraStep): the WormHUD
   * objects run their observed steps up to that tick now, so the arrow's
   * `CameraMan::CentreActivityTarget` (UpdateTransitionState) reaches the camera before the next
   * tick's camera step, as in the original (gdb, RenderFidelity.md "Camera"). */
  stepWormsTo(tick: number, camera: CameraMan): void {
    this.camTop = camera.bounds().tr.y;
    this.stepWorms(tick, camera);
  }

  /** WormHUDMan's objects (labels, damage numbers, arrow) once per tick of every observed step
   * up to `to` (the frame's update: all of them): unlike the IP2 scene they are not presented
   * two frames late (hb-A 3640–3650: the label keeps its place on the worm within 1 px while the
   * camera moves 20–27 px per frame). After a snapshot restore
   * WormHUDPanel::UpdateTransitionState's state comes from the sim's: the current worm's label
   * off screen after an input less than 10 s old in a turn (not in the hot seat), the arrow on
   * in the hot seat or in a turn with the label shown unless the select arrow is on, already
   * dropped in. */
  private stepWorms(to = Infinity, camera: CameraMan | null = null): void {
    let k = 0;
    for (; k < this.wormSteps.length; k++) {
      const st = this.wormSteps[k];
      if (st.h.tick > to) break;
      if (st.restored) {
        this.wormTick = st.h.tick;
        this.wormHud = st.h;
        const hotSeat = (st.turnFlags & 2) !== 0, live = this.labelsLive(st);
        const hidden = live && !hotSeat && this.recentInput(st, 10);
        this.panels.clear();
        if (hidden) this.panels.set(st.currentWorm, newPanel(PANEL_OFF_SCREEN, f32(25 + this.camTop)));
        this.enterRuns = -1;
        this.applyEnter(st);
        const on = (hotSeat || (live && !hidden)) && !st.h.enter.select;
        this.arrow = { active: on, state: ARROW_NONE, offset: 0, x: st.h.enter.gx, y: st.h.enter.gy, time: 0 };
        continue;
      }
      if (st.h.tick < this.wormTick) {
        this.wormTick = -1;
        this.panels.clear();
        this.arrow = { active: false, state: ARROW_NONE, offset: 0, x: 0, y: 0, time: 0 };
      }
      const n = this.wormTick < 0 ? 1 : Math.min(600, st.h.tick - this.wormTick);
      this.wormTick = st.h.tick;
      this.wormHud = st.h;
      for (let f = 0; f < n; f++) this.stepLabels(st, camera);
    }
    this.wormSteps.splice(0, k);
  }

  /** UpdateTransitionState's test for the current worm's transitions: in the turn, the hot seat,
   * or a retreat past its first instant (`IsRetreating() && GetSeconds() > 0`, DAT_0159af1c). */
  private labelsLive(st: StepInput): boolean {
    return (st.turnFlags & 3) !== 0 || ((st.turnFlags & 4) !== 0 && st.h.turnSecs > 0);
  }

  /** UICBInterface_ALL_SCENE::UpdateInTurn 0xea7060: the panels return after 5 s without input
   * and leave on the worm's first input. */
  private updateInTurn(h: HudView): void {
    if (h.inactive >= 5) this.showPanels(true);
    else if (h.flow & FLOW_ANY_INPUT) this.showPanels(false);
  }

  private showHotSeat(on: boolean): void {
    const it = this.scene?.find('MNU_Hotseat');
    if (it) it.active = on;
  }

  /** ShowHealthPanel / HideHealthPanel: MNU_Players slides 650 to the left. */
  private showPanels(on: boolean): void {
    if (on && (this.panelsState === ON || this.panelsState === TRANS_ON)) return;
    if (!on && (this.panelsState === OFF || this.panelsState === TRANS_OFF)) return;
    const players = this.scene?.find('MNU_Players');
    if (!players) return;
    players.active = true;
    this.panelsEndX = on ? this.panelsX : this.panelsX - 650;
    this.panelsState = on ? TRANS_ON : TRANS_OFF;
  }

  /** AnimatePlayerPanel (x lerp 0.125 per frame) and AnimateHealthPanel (each panel's y lerp
   * 0.125), the bars' scale (0.25 per frame). */
  private animatePanels(): void {
    const s = this.scene!;
    const players = s.get('MNU_Players');
    if (this.panelsState === TRANS_ON || this.panelsState === TRANS_OFF) {
      players.x += (this.panelsEndX - players.x) * 0.125;
      if (Math.abs(players.x - this.panelsEndX) <= 1) {
        players.x = this.panelsEndX;
        if (this.panelsState === TRANS_OFF) players.active = false;
        this.panelsState = this.panelsState === TRANS_ON ? ON : OFF;
      }
    }
    for (let i = 0; i < 6; i++) {
      const p = s.get(`MNU_Player_0${i + 1}`);
      p.y += (this.endY[i] - p.y) * 0.125;
      const bar = s.under(p, `MNU_HealthBar_0${i + 1}`);
      if (bar) bar.sx += (this.barEnd[i] - bar.sx) * 0.25;
    }
  }

  /** UpdateHealthBars: each bar's end scale = team health / the highest team health so far. */
  private updateBars(health: number[]): void {
    for (const hp of health) this.highest = Math.max(this.highest, hp);
    health.forEach((hp, i) => { if (i < 6) this.barEnd[i] = this.highest > 0 ? Math.max(0, hp) / this.highest : 0; });
  }

  /** SortHealthPanels: the living teams by health, ascending and stable; the lowest takes the
   * bottom location (MNU_Player_06's), the next the one above. Dead teams' panels are hidden. */
  private sortPanels(health: number[], h: HudView): void {
    const s = this.scene!;
    const order = health.map((hp, i) => ({ i, health: hp, alive: h.teams[i]?.alive ?? hp > 0 })).filter(t => t.alive && t.i < 6);
    for (let a = 0; a < order.length; a++) {
      for (let b = 0; b + 1 < order.length - a; b++) {
        if (order[b].health > order[b + 1].health) [order[b], order[b + 1]] = [order[b + 1], order[b]];
      }
    }
    for (let i = 0; i < 6; i++) s.get(`MNU_Player_0${i + 1}`).active = false;
    order.forEach((t, k) => {
      const p = s.get(`MNU_Player_0${t.i + 1}`);
      const first = !p.active && this.state === INIT;
      p.active = true;
      this.endY[t.i] = this.locations[5 - k].y;
      if (first) p.y = this.endY[t.i];
    });
  }

  /** FlashCurrentTeamHealthBar: the current team's bar cover pulses (event 9), the others' stop
   * (event 10). */
  private flashCurrentTeam(team: number): void {
    const s = this.scene!;
    for (let i = 0; i < 6; i++) {
      const c = s.find(`PLY_HealthBarCover_0${i + 1}`);
      if (c) s.trigger(c, i === team ? MSG_USER_1 : MSG_USER_2);
    }
  }

  // ---- commentary: CommentaryMan + UpdateCommentaryPanel 0xea7480 ----

  /** New comments of the sim's channel into CommentaryMan's queue (AddComment: the 4-entry
   * queue, a flush empties it) or its priority slot (AddPriorityComment overwrites it). */
  private takeComments(h: HudView, v: FrameView): void {
    if (h.nextComment < this.nextSeq) this.nextSeq = 0;
    // Every peer runs every press, but only the requester's machine shows its refusal in the
    // original (NetBranch.md Decisions): offline every team is local; a refusal without a team
    // (0xffffffff) shows.
    const local = (team: number): boolean => team === 0xffffffff || !this.teams.some(t => t.mine) || !!this.teams[team]?.mine;
    for (const c of h.comments) {
      if (c.seq < this.nextSeq) continue;
      if (c.kind === 'library' && c.comment >= COMMENT_REFUSALS && !local(c.team)) continue;
      const text = this.commentText(c, v);
      if (text === null) continue;
      if (c.kind === 'priority') this.prio = { text, time: c.delay, has: true, immediate: c.immediate };
      else {
        if (c.kind === 'library' && c.flush) this.queue = [];
        this.queue.push({ text, time: COMMENT_TIME });
        if (this.queue.length > COMMENT_QUEUE) this.queue.shift();
      }
    }
    this.nextSeq = h.nextComment;
  }

  private commentText(c: HudComment, v: FrameView): string | null {
    const ui = this.ui!;
    // AddPriorityComment's text as the original's producers made it: Gui::TextLibFind of the
    // sim's key (the magnet modes; the fuse units, PrintF'd with the seconds), or EquipGetTitle
    // 0xc33e50 of an equip name (its "Gui.WeaponPanel.<name>.Prompt" entry, else the name).
    if (c.kind === 'priority') {
      const t = ui.text(c.text) ?? ui.text(`Gui.WeaponPanel.${c.text}.Prompt`) ?? c.text;
      return c.arg ? t.replace('%s', c.arg) : t;
    }
    if (c.kind === 'collected') {
      const name = c.ingredient ? ui.text(INGREDIENT_TITLES[c.equip] ?? '') : this.equipTitle(c.equip);
      const t = ui.text(c.quantity >= INFINITE_AMMO ? 'Com.Crate.Collected_Infinite' : 'Com.Crate.Collected');
      return t === null ? null : t.replace('%Equip%', name ?? '').replace('%Quantity%', String(c.quantity));
    }
    const prefix = COMMENT_PREFIX[c.comment];
    let t = prefix ? ui.text(`${prefix}${c.choice + 1}`) : null;
    if (t === null) return null;
    const NONE = 0xffffffff;
    if (c.worm !== NONE) t = t.replace(/%WormName%/g, v.worms.find(w => w.index === c.worm)?.name ?? '');
    if (c.team !== NONE) t = t.replace(/%TeamName%/g, this.teams[c.team]?.name ?? '');
    if (c.vehicle !== NONE) t = t.replace(/%VehicleType%/g, ui.text(`Gui.Vehicle.${VEHICLE_NAMES[c.vehicle] ?? 'None'}`) ?? '');
    return t;
  }

  /** CommentaryMan::PopPriorityMessage 0xb96880: the slot's message; both flags clear. */
  private popPriority(): CommentEntry {
    this.prio.has = false;
    this.prio.immediate = false;
    return { text: this.prio.text, time: this.prio.time };
  }

  /** UICBInterface_ALL_SCENE::UpdateCommentaryPanel 0xea6610. The transitions read the slides'
   * `m_last_time` (`runTime`): SlideOn and SlideOff (events 10 and 9: a 20-frame move, then a NOP
   * to 21) have processed time 20. The display time counts from the end of the SlideOn on the
   * frame clock in float32. The end of the SlideOff deactivates the panel (its resting place,
   * y 124, is partly on screen). A non-immediate priority comment while the panel is on or
   * coming on only switches the state to its way off, without a SlideOff, as in the original;
   * the immediate checks read m_bImmediatePriority alone (HasImmediatePriorityMessage). */
  private updateCommentary(): void {
    const s = this.scene!;
    const panel = s.get('MNU_Commentary_01');
    const text = s.get('TXT_Commentary_01');
    const show = (c: CommentEntry): void => { text.text = c.text; this.commentDelay = c.time; };
    switch (this.commentState) {
      case ON:
        if (this.prio.has) {
          if (this.prio.immediate) { show(this.popPriority()); this.commentStart = this.time; }
          else this.commentState = TRANS_OFF;
        }
        if (this.commentDelay <= Math.fround(this.time - this.commentStart)) {
          s.trigger(panel, MSG_USER_1);
          this.commentState = TRANS_OFF;
        }
        break;
      case OFF: {
        const c = this.prio.has ? this.popPriority() : this.queue.shift();
        if (!c) break;
        panel.active = true;
        show(c);
        s.trigger(panel, MSG_USER_2);
        this.commentState = TRANS_ON;
        break;
      }
      case TRANS_ON:
        if (this.prio.has) {
          if (this.prio.immediate) { show(this.popPriority()); this.commentStart = this.time; }
          else this.commentState = TRANS_OFF;
        }
        if (s.runTime(panel, MSG_USER_2) >= 20) {
          this.commentState = ON;
          this.commentStart = this.time;
        }
        break;
      case TRANS_OFF:
        if (this.prio.immediate) {
          show(this.popPriority());
          this.commentStart = this.time;
          this.commentState = TRANS_ON;
          s.trigger(panel, MSG_USER_2);
        } else if (s.runTime(panel, MSG_USER_1) >= 20) {
          this.commentState = OFF;
          panel.active = false;
        }
        break;
      default:
        break;
    }
  }

  // ---- per-draw values: UpdateHealthPanel, UpdateTurnTimer 0xea6c80, the hot seat panel ----

  private refresh(v: FrameView, h: HudView, ui: UiData): void {
    const s = this.scene!;
    // team panels (NDS bindings: In_Game_Team.Team or User, colour coding, User.Picture)
    const leader = this.leader(v, h);
    for (let i = 0; i < 6; i++) {
      const p = s.get(`MNU_Player_0${i + 1}`);
      const info = this.teams[i];
      const name = s.under(p, `TXT_PlayerName_0${i + 1}`);
      // NDSPRIMER_Team_or_User: online the team's user, else (offline, an AI team) its name.
      if (name) name.text = this.users[i] ?? info?.name ?? `Team ${i + 1}`;
      const bar = s.under(p, `PLY_HealthBar_0${i + 1}`);
      if (bar) {
        const c = flat(info?.colour ?? i);
        setColour(bar, [c[0], c[1], c[2], bar.colours[0][3]]);
      }
      // The team's picture (`teamPicture`: the first team's is the transparent BlankTexture).
      const icon = s.under(p, `TEX_PlayerIconSmall_0${i + 1}`);
      if (icon) { icon.active = true; icon.tex = teamPicture(i); }
      const crown = s.under(p, `MNU_TopPlayerSmall_0${i + 1}`);
      if (crown) crown.active = i === leader;
    }
    // the current team's colour on the timer
    const cur = s.get('TEX_CurrentTeamTurn');
    setColour(cur, flat(this.teams[v.currentTeam]?.colour ?? v.currentTeam));
    cur.active = v.currentTeam >= 0;
    // turn timer
    const timer = s.get('TXT_TurnTime_01');
    const teleport = (h.flow & FLOW_TELEPORTING) !== 0, hotSeat = (v.turnFlags & 2) !== 0;
    const secs = h.turnSecs;
    if (this.state === IN_TURN || this.state === IN_RETREAT || teleport) {
      timer.text = secs <= 0 || (teleport && hotSeat) ? '00' : pad2(Math.trunc(secs) + 1);
      setColour(timer, dcol(0xffffffff));
    } else {
      timer.text = String(h.turnTime);
      setColour(timer, dcol(0x9d9d7dff));
    }
    let scale = 1;
    const flash = !(secs > 5 || (this.state !== IN_TURN && !(teleport && !hotSeat)) || secs < 0.1);
    if (flash) {
      // PulseTimer / FlashTimer 0xea6c20: the second's second half shrinks the text and fades
      // it from white to red.
      const f = secs - Math.floor(secs) - 0.5;
      if (f >= 0) {
        const c = Math.trunc(f * 2 * -255 + 255) & 0xff;
        setColour(timer, dcol(((c << 24) | (c << 16) | 0xffff) >>> 0));
        scale = 1 + f;
      }
    }
    timer.sx = timer.sy = scale;
    // round clock (StringFormatHelper::MilliSecondsToTimeString kMMSS)
    const round = s.get('TXT_RoundTime_01');
    if (h.roundSecs <= 0) round.text = '00:00';
    else if (h.infiniteRound) round.text = ' ';
    else {
      const t = Math.trunc(h.roundSecs);
      round.text = `${pad2(Math.trunc(t / 60))}:${pad2(t % 60)}`;
    }
    // hot seat panel
    const hs = s.find('MNU_Hotseat');
    if (hs?.active) {
      const col = flat(this.teams[v.currentTeam]?.colour ?? v.currentTeam);
      const team = s.under(hs, 'TXT_HotseatTeam_01'), user = s.under(hs, 'TXT_HotseatUser_01'), time = s.under(hs, 'TXT_HotseatTime_01');
      if (team) { team.text = this.teams[v.currentTeam]?.name ?? ''; setColour(team, col); }
      // NDSPRIMER_User_or_Prepare: online the team's user; offline and for a team without a user
      // "Prepare To Fire!" (Gui.Hotseat.Prepare, text library d6d39baf).
      if (user) { user.text = this.users[v.currentTeam] ?? ui.strings.d6d39baf ?? ''; setColour(user, col); }
      if (time) { time.text = String(Math.max(0, Math.ceil(secs))); setColour(time, col); }
      const crown = s.under(hs, 'MNU_TopPlayer_01');
      if (crown) crown.active = v.currentTeam === leader;
      // The team's picture as on its panel (match-end-A 4400: Bravo, team 1, HU_PlayerAvatar_01).
      const icon = s.under(hs, 'TEX_PlayerIconLarge_01');
      if (icon) { icon.active = v.currentTeam >= 0; icon.tex = teamPicture(v.currentTeam); }
    }
  }

  /** BaseRoundStats' leading team: the unique highest living health (none on a tie). */
  private leader(v: FrameView, h: HudView): number {
    let best = -1, top = -1, tie = false;
    v.teams.forEach((t, i) => {
      if (!(h.teams[i]?.alive ?? t.health > 0)) return;
      if (t.health > top) { top = t.health; best = i; tie = false; } else if (t.health === top) tie = true;
    });
    return tie ? -1 : best;
  }

  // ---- worm labels, damage numbers and the arrow (WormHUDMan) ----

  /** One tick of the WormHUD objects: WormHUDMan::LogicUpdate 0xb300f0's current arrow, then
   * WormHUDPanel::UpdateTransitionState 0xbfe680 and the panels' LogicUpdate 0xbfeb40, then
   * WormHUDCurrentArrow::LogicUpdate 0xcdbab0.
   * - WormHUDMan::LogicUpdate: while the worm-select arrow is on (WormHUDSelect::IsArrowActive,
   *   the WMH5 view's `enter.select`) the arrow switches off (k_MovingOff from its place).
   * - A worm that is not the current worm: k_MovingOn; leaving k_JetPacking, its panel shows the
   *   worm's health again (`m_uWormHealth = GetHealth()`).
   * - The current worm with a jetpack (`m_GadgetParam.m_Output.m_fJetPackFuel` != -1, read by
   *   the panels' update: hud.rs PanelVehicle `fuel`): k_JetPacking, and with the weapons panel
   *   open or on any input the arrow off.
   * - The current worm outside the turn, the hot seat and a retreat's first instant: k_MovingOn.
   *   Inside: from k_JetPacking straight to k_OffScreen (where the panel stands: SetPanelState's
   *   plain case) with the worm's health again; else after 10 s without input (DAT_0158ee5c) or
   *   in the hot seat the arrow switches on unless it or the select arrow is on (SetIsActive
   *   0xcdb500: k_MovingOn from 100 (DAT_0159af18) above the bounds' top; the camera, unless
   *   manual, `CentreActivityTarget`s from where it is: `camera`, the tick's camera step already
   *   run) and follows the worm (SetPosition of `enter.gx`/`gy` every tick), the label
   *   k_MovingOn; else on any input (or with the weapons
   *   panel open) the label k_MovingOff and the arrow off (k_MovingOff from its place). Not
   *   ported: the targeting and worm-state exits.
   * - SetPanelState 0xbfe590: k_OnScreen ignores k_MovingOn, k_OffScreen ignores k_MovingOff.
   * - Panels: k_JetPacking keeps m_fMovePosY at 0 and shows `(uint32)(long)fuel` as the health
   *   number (ut4 2809: "99"); k_MovingOn eases m_fMovePosY to 0 by 0.1 per tick (k_OnScreen
   *   within 1), k_MovingOff towards the bounds' top by 0.005 (DAT_0159bd50; k_OffScreen within
   *   25, DAT_015ad7e8).
   * - The arrow: k_MovingOn eases its offset to 0 by 0.1 (done within 1), k_MovingOff towards a
   *   quarter of the bounds' top (DAT_0159bd34; done within 1); visible while active or moving
   *   off. */
  private stepLabels(st: StepInput, camera: CameraMan | null): void {
    const h = st.h, top = this.camTop, a = this.arrow;
    if (h.enter.select && a.active) Object.assign(a, { active: false, state: ARROW_MOVING_OFF, offset: 0 });
    const set = (p: WormPanel, s: number): void => {
      if (p.state === PANEL_ON_SCREEN ? s !== PANEL_MOVING_ON : p.state !== PANEL_OFF_SCREEN || s !== PANEL_MOVING_OFF) p.state = s;
    };
    const arrowOff = (): void => {
      if (a.active) Object.assign(a, { active: false, state: ARROW_MOVING_OFF, offset: 0 });
    };
    for (const [i, p] of this.panels) {
      if (i === st.currentWorm) continue;
      if (p.state === PANEL_JETPACKING) p.health = -1;
      set(p, PANEL_MOVING_ON);
    }
    const fuel = h.enter.worm === st.currentWorm ? h.enter.fuel : -1;
    if (st.currentWorm >= 0) {
      let p = this.panels.get(st.currentWorm);
      if (!p) this.panels.set(st.currentWorm, p = newPanel());
      const leave = this.panelOpen() || (h.flow & FLOW_ANY_INPUT) !== 0;
      if (fuel !== -1) {
        p.state = PANEL_JETPACKING;
        if (leave) arrowOff();
      } else if (!this.labelsLive(st)) set(p, PANEL_MOVING_ON);
      else if (p.state === PANEL_JETPACKING) {
        p.state = PANEL_OFF_SCREEN;
        p.health = -1;
      } else if (h.inactive > 10 || (st.turnFlags & 2) !== 0) {
        if (!a.active && !h.enter.select) {
          Object.assign(a, { active: true, state: ARROW_MOVING_ON, offset: f32(100 + top), time: 0 });
          camera?.hudCentre();
        }
        [a.x, a.y] = [h.enter.gx, h.enter.gy];
        set(p, PANEL_MOVING_ON);
      } else if (leave) {
        set(p, PANEL_MOVING_OFF);
        arrowOff();
      }
    }
    this.applyEnter(st);
    const k = f32(0.1);
    for (const p of this.panels.values()) {
      if (p.state === PANEL_JETPACKING) {
        p.move = 0;
        p.health = Math.trunc(fuel) >>> 0;
        p.tick = h.tick;
      } else if (p.state === PANEL_MOVING_ON) {
        p.move = f32(f32(f32(0 - p.move) * k) + p.move);
        if (f32(p.move - 1) <= 0) p.state = PANEL_ON_SCREEN;
      } else if (p.state === PANEL_MOVING_OFF) {
        p.move = f32(f32(f32(top - p.move) * f32(0.005)) + p.move);
        if (top <= f32(p.move + 25)) p.state = PANEL_OFF_SCREEN;
      }
    }
    if (a.state === ARROW_MOVING_ON) {
      a.offset = f32(f32(f32(0 - a.offset) * k) + a.offset);
      if (a.offset <= 1) { a.state = ARROW_NONE; a.offset = 0; }
    } else if (a.state === ARROW_MOVING_OFF) {
      const dest = f32(0.25 * top);
      a.offset = f32(f32(f32(dest - a.offset) * k) + a.offset);
      if (f32(dest - 1) <= a.offset) a.state = ARROW_NONE;
    }
    if (a.active || a.state === ARROW_MOVING_OFF) a.time += 1 / 60;
  }

  /** UpdateTransitionState 0xbfe680's vehicle block, at the panels' LogicUpdate of the step's
   * last tick (the sim's PanelVehicle; nothing when it did not run): every other panel
   * m_bDrawEnterVehicle = false; the current worm's panel (when its LogicUpdate ran)
   * m_CurrentVehicleIndex = GetVehicleToEnter and the prompt for a vehicle that is in play and not
   * pending removal, of a team that is not AI, when the worm is local (NetSetup::IsLocalWorm: in
   * a network game a team of this machine). */
  private applyEnter(st: StepInput): void {
    const e = st.h.enter, NONE = 0xffffffff;
    if (e.runs === this.enterRuns) return;
    this.enterRuns = e.runs;
    for (const [i, p] of this.panels) if (i !== e.worm) p.drawEnter = false;
    if (e.worm === NONE || !e.ran) return;
    let p = this.panels.get(e.worm);
    if (!p) this.panels.set(e.worm, p = newPanel());
    p.vehicle = e.vehicle;
    const local = !this.teams.some(t => t.mine) || !!this.teams[e.team]?.mine;
    p.drawEnter = e.vehicle !== NONE && e.found && (e.cached & 3) === 1 && !e.teamAI && local;
    if (p.drawEnter) [p.offset, p.vx, p.vy, p.vid] = [e.offset, e.x, e.y, e.id];
  }

  /** WormHUDPanel::GraphicUpdate 0xbff1a0's prompt part for the current worm's panel: the panel
   * visibility (k_JetPacking: not WormMan::IsWormHiddingFromLocalPlayer; else IsLabelVisible, an
   * active task, not slot 79 IsDead, not HasSurrendered, not slot 81 IsDrowning), the capture
   * dropped when m_CurrentVehicleIndex changed, then DrawEnterVehicle 0xbfedd0: the vehicle's
   * position raised by HUDEnterOffset x (1 - 0.4 (1 - CameraMan::GetNormalisedZoom)), at its
   * WorldToHUD; "Gui.Vehicle.Enter<VehicleGetName>" with its control tokens replaced, in
   * OrnitonsSerial-Regular 24, white over a black shadow 1 right and 1 down. The capture of the
   * two prints is drawn centred on that point; the frame that makes it centres it on the point
   * moved left by half the string width and up by the point size (the prints' own place). Not
   * ported: the panel's k_HideAll state (the WormPanelNext cycle is not in the client) and the
   * 24-capture submission limit. */
  private drawEnterVehicle(h: HudView, r: Renderer, p: Ip2Painter, ui: UiData): void {
    const e = h.enter;
    const pn = this.panels.get(e.worm);
    if (!pn) return;
    const vis = e.visible;
    const jetpack = (vis & ENTER_JETPACK) !== 0;
    const mine = !this.teams.some(t => t.mine) || !!this.teams[e.team]?.mine;
    const visible = jetpack ? (vis & ENTER_LABEL) !== 0 || (mine && !e.teamAI)
      : (vis & (ENTER_LABEL | ENTER_ACTIVE)) === (ENTER_LABEL | ENTER_ACTIVE) && (vis & (ENTER_DEAD | ENTER_SURRENDERED | ENTER_DROWNING)) === 0;
    if (!visible) return;
    if (pn.vehicle !== pn.lastVehicle) {
      pn.lastVehicle = pn.vehicle;
      pn.capture = false;
    }
    if (!pn.drawEnter) return;
    const mult = 1 - 0.4 * (1 - r.cameraMan.pose().zoomParam);
    const at = this.toHud(r, p, pn.vx, f32(pn.vy + f32(mult * pn.offset)));
    const key = `Gui.Vehicle.Enter${VEHICLE_NAMES[pn.vid] ?? '[unknown vehicle]'}`;
    const text = replaceControlTokens(ui.text(key) ?? key, k => ui.text(k));
    const box = p.inkBox(text, F_ORNITONS24);
    let [cx, cy] = [at.x, at.y];
    if (!pn.capture) {
      pn.capture = true;
      [cx, cy] = [at.x - p.textWidth(text, F_ORNITONS24) / p.s / 2, at.y + 24];
    }
    // The capture's area: both prints' glyph quads (the shadow 1 right, 1 down).
    const pen = p.toCanvas(cx - (box.l + box.r + 1) / 2, cy - (box.t + box.b - 1) / 2);
    p.text(text, F_ORNITONS24, [pen.x, pen.y, 0, 0], [1, 1, 1, 1], 0, false, false, 0, { dx: 1, dy: 1, col: [0, 0, 0, 1] });
  }

  /** Design-space (HUD) position of a world point: WormHUDMan::WorldToHUD. */
  private toHud(r: Renderer, p: Ip2Painter, x: number, y: number): { x: number; y: number } {
    const sc = r.worldToScreen(x, y);
    return { x: (sc.x - p.ox) / p.s - DESIGN_W / 2, y: DESIGN_H / 2 - (sc.y - p.oy) / p.s };
  }

  private drawLabels(v: FrameView, h: HudView, r: Renderer, p: Ip2Painter): void {
    const ui = this.ui!;
    // WormHUDDamage::GraphicUpdate 0xb99500: WMD-SerifBold 20, centred, at WorldToHUD(position).
    for (const d of h.damages) {
      if (d.done) continue;
      const at = this.toHud(r, p, d.pos[0], d.pos[1]);
      const c = p.toCanvas(at.x, at.y);
      p.text(d.text, F_SERIF20, [c.x, c.y, 0, 0], d.colour as Rgba, 1, false, false, 0, null);
    }
    // WormHUDMan::LogicUpdate 0xb300f0: from a normalised camera zoom of 0.8 (DAT_015a9a00) out,
    // the panels show the health only (k_HealthOnly); k_NoTeam otherwise.
    const healthOnly = r.cameraMan.pose().zoomParam >= 0.8;
    for (const w of v.worms) {
      // m_uWormHealth (LogicUpdate 0xbfeb40): set at the panel's first update, then counted 1
      // per tick (the 0.01-s step, DAT_0159bd48, is shorter than a tick) towards the health; in
      // k_JetPacking the fuel (stepLabels).
      let pn = this.panels.get(w.index);
      if (!pn) this.panels.set(w.index, pn = newPanel());
      if (pn.state !== PANEL_JETPACKING) {
        if (pn.health < 0 || v.tick < pn.tick) pn.health = w.health;
        else pn.health += Math.sign(w.health - pn.health) * Math.min(v.tick - pn.tick, Math.abs(w.health - pn.health));
        pn.tick = v.tick;
      }
      // GraphicUpdate 0xbff1a0: in k_JetPacking only WormMan::IsWormHiddingFromLocalPlayer hides
      // the panel (the jetpacking worm is the current worm: the widgets' inputs).
      const shown = pn.state === PANEL_JETPACKING ? !hiddenFromLocal(v.widgets, t => r.isLocalTeam(t))
        : !(w.flags & WORM_GRAVESTONE || w.health <= 0 || pn.state === PANEL_OFF_SCREEN);
      if (shown) this.drawLabel(w, pn, r, p, ui, healthOnly);
    }
    this.drawEnterVehicle(h, r, p, ui);
    // WormHUDCurrentArrow::GraphicUpdate 0xcdb600: HU_TurnArrow_01 (looping "Go", tinted with the
    // team's flat colour) in the GUI pass at WorldToHUD(m_vPosition + m_vOffset) (z 1) + 175
    // (DAT_015a97c0) up.
    const a = this.arrow;
    const on = a.active || a.state === ARROW_MOVING_OFF;
    const at = r.worldToHud(a.x, f32(a.y + a.offset), 1);
    const tint = flat(this.teams[v.currentTeam]?.colour ?? v.currentTeam);
    r.hudMesh('turnArrow', 'HU_TurnArrow_01', on ? at.x : 0, on ? at.y + 175 : 0, on, 'Go', a.time % ARROW_LOOP, [tint[0], tint[1], tint[2]]);
    // The gadget aim and targeting widgets (game/widgets.ts).
    const team = (t: number): [number, number, number] => { const c = flat(this.teams[t]?.colour ?? t); return [c[0], c[1], c[2]]; };
    this.widgets.draw3d(v, r, team, t => r.isLocalTeam(t));
    this.widgets.draw2d(v, r, {
      toCanvas: (x, y) => p.toCanvas(x, y), s: p.s,
      textWidth: t => p.textWidth(t, F_SERIF20) / p.s,
      texture: (ref, rect, c) => { const tx = ui.texture(ref); if (tx) p.texture(tx, rect, [c]); },
      text: (t, b, c) => p.text(t, F_SERIF20, [b[0], b[1] + b[3] * 0.75, b[2], 0], c, 1, false, false, 0, { dx: 1, dy: 1, col: [0, 0, 0, 0.588] }),
      equipIcon: e => this.panelInfo?.equip[e]?.icon ?? null,
      size: ref => { const tx = ui.texture(ref); return tx ? { w: tx.sw, h: tx.sh } : null; },
      texInfo: ref => { const tx = ui.texture(ref); return tx ? { w: tx.sw, h: tx.sh, crop: tx.crop } : null; },
      sprite: (ref, m, c) => {
        const tx = ui.texture(ref), k = p.s;
        if (tx) p.affine(tx, [m[0] * k, -m[1] * k, m[2] * k, -m[3] * k, p.ox + (m[4] + DESIGN_W / 2) * k, p.oy + (DESIGN_H / 2 - m[5]) * k], c);
      },
      print: (t, font, x, y, c) => { const at = p.toCanvas(x, y); p.text(t, font, [at.x, at.y, 0, 0], c, 0, false, false, 0, null); },
      fontWidth: (t, font) => p.textWidth(t, font) / p.s,
      fontHeight: font => p.fontHeight(font),
    }, team);
  }

  /** WormHUDPanel (k_NoTeam; k_HealthOnly drops the name): the name box over the health box, the team colour's text in
   * WMD-SerifBold 20 on the HU_NamePanel caps and middle. GraphicUpdate 0xbff1a0 puts the name
   * box 85 (DAT_015b7080) + m_fMovePosY above the worm and the health box the box texture's
   * height (32) below it; DrawInfoPanel lays a box out as the text width + 20 (DAT_015a8bf4)
   * between the 8-px caps, the text centred with its baseline a quarter of the box up and a 0.588
   * black shadow. The health is the panel's counted `m_uWormHealth`. In k_JetPacking (the fuel
   * as the health, ut4 2809) the name box is skipped and the health box goes half a box lower:
   * its y is 85 + h/2 - 1.5 h instead of 85 + m_fMovePosY + h - 1.5 h (h = 32, DAT_01590164 0.5,
   * DAT_015aa4d8 1.5). The worm's position is Worm::GetGraphicalPosition 0xc55c80: the physics
   * position outside a vehicle (the arrow uses it too). */
  private drawLabel(w: WormView, pn: WormPanel, r: Renderer, p: Ip2Painter, ui: UiData, healthOnly: boolean): void {
    const [gx, gy] = graphicalPosition(w);
    const at = this.toHud(r, p, gx, gy);
    const col = flat(this.teams[w.team]?.colour ?? w.team);
    const health = pn.health >= 1000 ? (ui.text('Gui.Common.Infinite') ?? '∞') : String(pn.health);
    const left = ui.texture('HU_NamePanel_01.png'), middle = ui.texture('HU_NamePanel_02.png'), right = ui.texture('HU_NamePanel_03.png');
    const white: Rgba[] = [[1, 1, 1, 1]];
    const rows: [string, number][] = pn.state === PANEL_JETPACKING ? [[health, 85 - LABEL_BOX_H * 1.5]]
      : healthOnly ? [[health, pn.move + 85 - LABEL_BOX_H]] : [[w.name, pn.move + 85], [health, pn.move + 85 - LABEL_BOX_H]];
    for (const [text, bottom] of rows) {
      const bw = p.textWidth(text, F_SERIF20) / p.s + 20;
      const tl = p.toCanvas(at.x - bw / 2, at.y + bottom + LABEL_BOX_H), br = p.toCanvas(at.x + bw / 2, at.y + bottom);
      const box: [number, number, number, number] = [tl.x, tl.y, br.x - tl.x, br.y - tl.y];
      const capW = 8 * p.s;
      if (left) p.texture(left, [box[0] - capW, box[1], capW, box[3]], white);
      if (middle) p.texture(middle, box, white);
      if (right) p.texture(right, [box[0] + box[2], box[1], capW, box[3]], white);
      const base = p.toCanvas(0, at.y + bottom + LABEL_BOX_H / 4).y;
      p.text(text, F_SERIF20, [box[0], base, box[2], 0], col, 1, false, false, 0, { dx: 1, dy: 1, col: [0, 0, 0, 0.588] });
    }
  }

  /** UpdateWindArrows 0xea8190 (in the InTurn and InHotSeat states): when floor(10 x
   * BaseTurnStats::GetWindSpeed) changes, XOM_Wind gets the proposed animation
   * "WindSpeed%s%02u": "r"/"l" and the magnitude truncated, clamped to 10 (DAT_0158ee5c,
   * DAT_015d9ed4 = -10), no side for 0. `__UpdateProposed` starts it from 0 and, the item's
   * m_bCurrentLooping being false, it plays once and holds its last frame: the 50-s clip grows
   * the arrows in and scrolls the clouds. Before the first call the item plays its default
   * "WindSpeedl10" (looping). */
  private updateWindArrows(wind: number): void {
    let x = 10 * wind;
    let f = Math.floor(x);
    if (f === this.prevWind) return;
    let side: string, n: number;
    if (x < 0 || x < 10) {
      if (x < -10) { f = -10; x = -10; }
      n = Math.trunc(Math.abs(x));
      side = n === 0 ? '' : x < 0 ? 'l' : 'r';
      this.prevWind = f;
    } else {
      side = 'r';
      n = 10;
      this.prevWind = 10;
    }
    const it = this.scene?.find('XOM_Wind');
    if (!it) return;
    it.xomAnim = `WindSpeed${side}${String(n).padStart(2, '0')}`;
    it.xomLoop = false;
    it.xomTime = 0;
  }

  /** cIP2_2DXom::Render 0xf6f2d0 for an item in the painter's layer order: an item with its own
   * capture (no parent node) through UiXom (its camera, current animation and composite mode,
   * the items attached to its nodes inside it), the quad in the item's colour. Attached items
   * draw nothing of their own. */
  private drawXom(it: Item, box: [number, number, number, number]): void {
    const x = it.sections.xom as XomSection | undefined, ui = this.ui;
    if (!x || !ui || !this.xom || (x.m_ParentXOMID && x.m_ParentNodeName)) return;
    const xdesc = ui.mesh(it.resId);
    if (!xdesc) return;
    const scene = this.results?.items.includes(it) ? 'results' : 'hud';
    const composite = x.m_CompositeMode === 1 ? 1 : x.m_CompositeMode === 2 ? 2 : 0;
    this.xom.draw(this.ctx, {
      key: `${scene}:${it.key}`, xdesc, designW: it.pw, designH: it.ph,
      camera: [x.m_CameraPositionX ?? 0, x.m_CameraPositionY ?? 0, x.m_CameraPositionZ ?? 0],
      clip: it.xomAnim, time: it.xomTime, loop: it.xomLoop, composite, attach: this.xomAttached.get(it),
    }, box, it.pColours[0]);
  }

  /** The results' WINS (NUI_Cache team wins): the wins of the team's lobby slot in the room's
   * earlier matches (the server's tally, sent with the match teams) plus this round's
   * (BaseRoundLogic::Update_Victory1 adds one to every team of the victorious alliance when it
   * sets m_NonLogicalGameEnded). Both are state, not an edge the client must have stepped
   * through, so a client that joins during the round or the results shows the same count. */
  private wins(h: HudView, team: number): number {
    const t = h.teams[team];
    const now = h.roundEnded && t && t.alliance === h.victorious ? 1 : 0;
    return (this.teams[team]?.wins ?? 0) + now;
  }

  // ---- the weapons panel (cIP2_WP_Frame::ConstructWeaponsPanel 0xdba3d0) ----

  private buildWeaponsPanel(s: Scene, info: PanelInfo, h: HudView): void {
    const host = s.get('MNU_WeaponsPanel');
    for (const c of [...host.children]) {
      host.children = host.children.filter(x => x !== c);
      s.items = s.items.filter(x => x !== c && !this.descends(x, c));
    }
    const num = (k: string, d: number): number => Number(new RegExp(`${k}\\s*=\\s*(\\d+)`).exec(info.script)?.[1] ?? d);
    const ncols = num('NumCols', 7), cell = num('CellSize', 48), titleH = num('TitleHeight', 30);
    const orderBlock = /EquipOrder\s*=\s*\{([^}]*)\}/.exec(info.script.replace(/\/\/[^\n]*/g, ''))?.[1] ?? '';
    const byName = new Map(info.equip.map((e, i) => [e.name.toLowerCase(), i] as const));
    const order = orderBlock.split(';').map(t => t.trim()).filter(Boolean).map(n => {
      const e = byName.get(n.toLowerCase()) ?? 134;
      return e === 134 ? NOTHING : e;
    });
    const craftRows = 1;
    const nbase = Math.floor(order.length / ncols);
    const W = cell * ncols, H = (nbase + craftRows) * cell + titleH + 6 + 51 + 4 + 8 + 8;
    const W2 = W / 2, H2 = H / 2, C2 = cell / 2;
    host.active = false;
    const gen = (type: number, name: string, parent: Item, layer: number, x: number, y: number, w: number, hh: number, res = 0): Item => {
      const i = new Item();
      i.type = type;
      i.id = type << 16;
      i.name = name;
      i.layer = layer;
      [i.x, i.y, i.w, i.h] = [x, y, w, hh];
      i.inheritColour = false;
      if (res) i.resId = RES(res);
      return s.addChild(parent, i);
    };
    const frame = gen(7026, 'WP_Frame', host, 5, 750, craftRows * C2, W, H);
    frame.active = false;
    this.frameTex.clear();
    const border = (name: string, x: number, y: number, w: number, hh: number): void => {
      this.frameTex.set(name, gen(T_TEX, name, frame, 14, x, y, w, hh, FRAME_TEX[name][0]));
    };
    const tmW = W - 212 + 13;
    border('TitleBackerLeft', -(tmW / 2) - 107 / 2 + 1, H2 - 26, 107, 52);
    border('TitleBackerMiddle', 1, H2 - 26, tmW, 52);
    border('TitleBackerRight', 106 / 2 + tmW / 2 + 1, H2 - 26, 106, 52);
    const title = gen(T_TXT, 'TitleBox', frame, 15, 18, H2 - 26 + 9, W, titleH);
    title.sections.txt = { m_FontID: F_BLITZ16, m_Align: 0 };
    setColour(title, dcol(0x7e592cff));
    this.titleBox = title;
    const y0 = -52;
    this.cells = order.map((e, i) => {
      const col = i % ncols, row = Math.floor(i / ncols);
      const it = gen(T_WPCELL, `WP_Cell[${i}]`, frame, 15, col * cell - (W2 - C2), H2 - C2 - row * cell + y0, cell, cell);
      return { item: it, equip: e, slot: null };
    });
    const y1 = y0 - cell * nbase;
    const dmW = W - 200;
    border('DividerLeft', -(dmW / 2) - 50, H2 + y1 - 13.5, 100, 27);
    border('DividerMiddle', 0, H2 + y1 - 13.5, dmW, 27);
    border('DividerRight', 50 + dmW / 2, H2 + y1 - 13.5, 100, 27);
    const y2 = y1 - 27;
    this.craftCells = [];
    for (let r = 0; r < craftRows; r++) {
      for (let c = 0; c < ncols; c++) {
        const it = gen(T_WPCELL, `WP_CraftingCell[${r * ncols + c}]`, frame, 15, c * cell - (W2 - C2), H2 - C2 - r * cell + y2, cell, cell);
        this.craftCells.push({ item: it, equip: NOTHING, slot: r * ncols + c });
      }
    }
    const q = 0.25 * W, x0 = 0.5 * q - W2 - 4, ybase = H2 + (y2 - craftRows * cell);
    this.ingredients = [WP.sauce, WP.paste, WP.seeds, WP.tears].map((t, k) => gen(T_TEX, `Ingredient${k}`, frame, 15, x0 + k * q, ybase - 23.5, 45, 47, t));
    const eq = gen(T_TAB, 'EquipTab', frame, 5, 4 - (W2 - 123 / 2), H2 + 37 / 2, 123, 37);
    eq.style = 'CraftPanelTab';
    eq.sections.TabButton = { m_LocaliseTitleToken: 'FE.WeaponsPanel.Title', m_FontID: F_BLITZ16, m_bFirstTab: true };
    eq.state = 'Selected';
    this.equipTab = eq;
    this.craftTab = null;
    if (h.craftingEnabled) {
      const ct = gen(T_TAB, 'CraftingTab', frame, 5, eq.x + eq.w, H2 + 37 / 2 - 2, 122, 34);
      ct.style = 'WeaponPanelTab';
      ct.sections.TabButton = { m_LocaliseTitleToken: 'FE.CraftingPanel.Title', m_FontID: F_BLITZ16 };
      ct.state = 'NotSelected';
      this.craftTab = ct;
    }
    const bmW = W - 112 + 14;
    border('BottomBorderLeft', -(bmW / 2) - 28, ybase - 37.5, 56, 75);
    border('BottomBorderMiddle', 0, ybase - 37.5, bmW, 75);
    border('BottomBorderRight', 28 + bmW / 2, ybase - 37.5, 56, 75);
    border('LeftBorder', -W2 - 4, -26, 8, H - 26);
    border('RightBorder', W2 + 4, -26, 8, H - 26);
    // The crafting menu (ConstructWeaponsPanel 0xdbb3df): left of the frame, the frame's height,
    // inactive until crafting mode. Its Render draws the button panel, the ingredients panel and
    // the ingredient lines (refreshMenu places them).
    const menu = gen(7028, 'WP_Crafting', frame, 5, -(W2 + CRAFT.menuW / 2 + 8), 0, CRAFT.menuW, H);
    menu.active = false;
    this.menu = menu;
    this.sidePanel = gen(T_TEX, 'ButtonPanel', menu, 5, 0, 0, 0, 0);
    this.ingredientsPanel = gen(T_TEX, 'IngredientsPanel', menu, 5, 0, 0, 0, 0, CRAFT.ingredients);
    this.ingredientLines = [0, 1, 2, 3].map(k => {
      const t = gen(T_TXT, `IngredientLine${k}`, menu, 5, 15, 0, 190, 25);
      t.sections.txt = { m_FontID: F_ORNITONS16, m_Align: 0, m_bVerticalAlignment: true, m_wrappable: true };
      return t;
    });
    // ConstructCraftingPanel 0xdcd100: the buttons (the menu's layer + IP2_LAYER_0_9) from
    // h / 2 - 44 down, 56 apart, the fourth (a dismantle button) 68 below the third;
    // ConstructButton: the icon backer (+ IP2_LAYER_0_9) and the equip icon (+ IP2_LAYER_0_8),
    // drawn over the backer in the captures (frame 3300). Then SetParentEquip(kEquip_Bazooka),
    // the menu inactive.
    const by = H2 - CRAFT.buttonH / 2 - 18, ix = -(CRAFT.backerW / 2 + CRAFT.buttonW / 2 + 2);
    this.buttons = [by, by - 56, by - 112, by - 180].map((y, k) => {
      const b = gen(T_CRAFT, `CraftingButton${k + 1}`, menu, 14, 30, y, CRAFT.buttonW, CRAFT.buttonH);
      b.style = 'Craft';
      b.sections.button = { m_FontID: F_BLITZ16, m_bOverrideColour: true };
      gen(T_TEX, 'IconBacker', b, 22, ix, 0, CRAFT.backerW, CRAFT.buttonH, CRAFT.backer);
      return { item: b, icon: gen(T_TEX, 'EquipIcon', b, 23, ix, 0, CRAFT.icon, CRAFT.icon), equip: NOTHING, dismantle: k === 3, cancel: false };
    });
    this.parentEquip = this.originalEquip = 0;
    this.variation = NOTHING;
    this.variationDismantle = this.menuCrafting = this.menuTextures = false;
    this.buttonPanel = 0;
    this.frame = frame;
    this.wpState = OFF;
    const tabs = [this.equipTab, this.craftTab, ...this.buttons.map(b => b.item)].filter(t => t !== null);
    void this.ui?.preload([...[WP.cell, WP.craftCell, WP.focus, WP.focusCraft, ...Object.values(FRAME_TEX).flat(), ...CRAFT.panels, CRAFT.ingredients, CRAFT.backer, CRAFT.cancelIcon,
      ...info.equip.flatMap(e => [e.icon, e.delayed, e.noAmmo]).filter(Boolean)].map(RES), ...tabs.flatMap(t => (this.ui ? widgetTextures(this.ui, t) : []))]);
  }

  private descends(x: Item, root: Item): boolean {
    for (let p = x.parent; p; p = p.parent) if (p === root) return true;
    return false;
  }

  /** cIP2_WP_Frame::Render 0xdb8c90: the frame slides in from x 750 and out again; off screen it
   * leaves crafting mode and deactivates. */
  private stepWeaponsPanel(): void {
    const f = this.frame;
    if (!f) return;
    if (this.wpState === TRANS_ON) {
      f.x += (0 - f.x) * 0.25;
      if (f.x - 0.05 <= 0) { f.x = 0; this.wpState = ON; }
    } else if (this.wpState === TRANS_OFF) {
      f.x += (750 - f.x) * 0.25;
      if (750 <= f.x + 1) {
        f.x = 750;
        this.wpState = OFF;
        this.setCraftingMode(false);
        f.active = false;
        f.parent!.active = false;
      }
    }
  }

  /** cIP2_WP_Frame::SetCraftingMode 0xdb8860 (also what the frame's LogicalUpdate 0xdbbe60 calls
   * when the selected tab and the mode differ): the border textures, the selected tab and the
   * crafting menu's activity (cIP2_WP_Crafting::SetIsActive 0xdcd750: UpdateCurrentLayout; its
   * SetFocusToDismantle is unreachable with the frame active). Without crafting the mode stays
   * off. Public for the tests (the captures select the tab from gdb). */
  setCraftingMode(on: boolean): void {
    const enabled = !!this.craftTab;
    this.crafting = on && enabled;
    for (const [name, it] of this.frameTex) it.resId = RES(FRAME_TEX[name][this.crafting ? 1 : 0]);
    if (this.equipTab) this.equipTab.state = this.crafting ? 'NotSelected' : 'Selected';
    if (this.craftTab) this.craftTab.state = this.crafting ? 'Selected' : 'NotSelected';
    if (enabled && this.menu) {
      this.menu.active = this.crafting;
      this.updateCurrentLayout();
    }
  }

  /** cIP2_WP_Crafting::SetCraftingVariation 0xdccae0 (a crafting button's FocusGain / FocusLoss):
   * the variation whose ingredients Render lists, or the dismantle button's equip. Public for the
   * tests (the captures set it from gdb, without the button's focus). */
  setCraftingVariation(equip: number, dismantle: boolean): void {
    this.variation = equip;
    this.variationDismantle = dismantle;
  }

  /** cIP2_WP_Crafting::SetParentEquip 0xdcd0d0 (cIP2_WP_Frame::SetCraftingWeapon): not while the
   * team crafts; the layout follows while the menu is active. */
  private setParentEquip(e: number): void {
    if (this.menuCrafting) return;
    this.parentEquip = this.originalEquip = e;
    if (this.menu?.active && this.frame?.active) this.updateCurrentLayout();
  }

  /** CraftingMan::GetRecipe 0xc6c930 (no reward equips here): the loaded recipe of `e`. */
  private recipe(e: number): Recipe | undefined {
    return this.recipes.find(r => r.product === e);
  }

  /** cIP2_WP_Crafting::UpdateCurrentLayout 0xdccaf0. While the team crafts: button 1 alone, the
   * cancel button, at 0.35 of the height, on the 2-button panel. Else by the parent recipe's
   * variation count (an upgrade's parent first): 0: the parent and the dismantle button on the
   * 2-button panel, 1: the parent, variation 0 and dismantle (3), 2: the parent, variations 0 and
   * 1 and dismantle (4); the dismantle button holds the cell's own equip (m_OriginialEquip). An
   * inactive menu or an invalid parent shows no buttons. */
  private updateCurrentLayout(): void {
    const menu = this.menu;
    if (!menu || this.buttons.length < 4) return;
    const [b1, b2, b3, b4] = this.buttons;
    const panel = (n: number): void => { this.buttonPanel = this.menuTextures ? CRAFT.panels[n] : 0; };
    const kind = (b: CraftButton, dismantle: boolean, cancel: boolean): void => {
      b.dismantle = dismantle;
      b.cancel = cancel;
      this.buttonLayout(b);
    };
    const equip = (b: CraftButton, e: number): void => {
      b.equip = e;
      this.buttonLayout(b);
    };
    const active = menu.active && !!this.frame?.active;
    if (active && this.menuCrafting) {
      for (const b of [b2, b3, b4]) b.item.active = false;
      b1.item.active = true;
      kind(b1, false, true);
      panel(0);
      b1.item.x = 30;
      b1.item.y = Math.fround(0.35) * menu.h;
      return;
    }
    if (!active || !equipValid(this.parentEquip)) {
      for (const b of this.buttons) b.item.active = false;
      return;
    }
    if (this.upgraded(this.parentEquip)) this.parentEquip = this.recipe(this.parentEquip)?.parent ?? NOTHING;
    const r = this.recipe(this.parentEquip);
    const n = r?.variations.length ?? 0;
    b1.item.x = 30;
    b1.item.y = menu.h / 2 - CRAFT.buttonH / 2 - 18;
    for (const b of this.buttons) b.item.active = false;
    for (const b of [b2, b3, b4]) kind(b, false, false);
    kind(b1, false, false);
    const used = n === 0 ? [b1, b2] : n === 1 ? [b1, b2, b3] : n === 2 ? this.buttons : [];
    if (!used.length) return;
    panel(used.length - 2);
    for (const b of used) b.item.active = true;
    const dis = used[used.length - 1];
    kind(dis, true, false);
    equip(b1, this.parentEquip);
    used.slice(1, -1).forEach((b, k) => equip(b, r?.variations[k] ?? NOTHING));
    equip(dis, this.originalEquip);
  }

  /** cIP2_WP_CraftingButton::UpdateLayout 0xdc9880, for a valid equip only: the cancel button's
   * "Gui.Common.Cancel" and HU_WPCraft1_01, the dismantle button's "FE.Dismantle.Title", else the
   * equip's title (EquipGetTitle); the equip's icon (EquipGetUIIconID). */
  private buttonLayout(b: CraftButton): void {
    if (!equipValid(b.equip)) return;
    const ui = this.ui;
    b.item.text = b.cancel ? ui?.text('Gui.Common.Cancel') ?? '' : b.dismantle ? ui?.text('FE.Dismantle.Title') ?? '' : this.equipTitle(b.equip) ?? '';
    b.icon.resId = RES(b.cancel ? CRAFT.cancelIcon : this.panelInfo?.equip[b.equip]?.icon ?? 0);
  }

  /** `EquipIsUpgraded` 0xc33c90: g_Info m_Flags bit 3. */
  private upgraded(e: number): boolean {
    return ((this.panelInfo?.equip[e]?.flags ?? 0) & 8) !== 0;
  }

  /** Whether a cavern map allows `e` here (`EquipIsAvailableInCaverns >= IsCavern`): the panel
   * team's CanCraftWeapon bit 0x80 says the opposite (unknown before the first answer). */
  private cavernAllows(e: number): boolean {
    return !this.craft || (this.craft.craft[e] & 0x80) === 0;
  }

  /** The cells' textures and icons, the title and the crafting menu (cIP2_WP_Cell::Render
   * 0xdcae80, SetupIconTexture: the no-ammo icon without ammo, the delayed one while delayed). */
  private refreshWeaponsPanel(h: HudView): void {
    const f = this.frame, info = this.panelInfo;
    if (!f || !info || !f.active) return;
    const t: HudTeam | undefined = h.teams[this.wpTeam];
    if (this.titleBox) this.titleBox.text = equipValid(this.titleEquip) ? this.equipTitle(this.titleEquip) ?? '' : ' ';
    // crafted weapons' slots
    for (const c of this.craftCells) {
      const slot = t?.slots.find(x => x.index === c.slot);
      c.equip = slot ? slot.equip : NOTHING;
    }
    const cellTex = RES(this.crafting ? WP.craftCell : WP.cell);
    for (const c of [...this.cells, ...this.craftCells]) {
      const e = info.equip[c.equip];
      const ammo = t?.ammo[c.equip] ?? 0, delay = t?.delay[c.equip] ?? 0;
      const icon = c.equip === NOTHING || !e ? undefined : ammo === 0 && c.slot === null ? e.noAmmo : delay > 0 ? e.delayed : e.icon;
      c.item.extra = { cell: cellTex, icon: icon ? RES(icon) : undefined, focus: this.focus === c ? RES(this.crafting ? WP.focusCraft : WP.focus) : undefined };
    }
    if (!this.menu?.active) return;
    // cIP2_WP_Crafting::LogicalUpdate 0xdcd560: SwitchMode(IsTeamCrafting || IsTeamDismantling);
    // turning on lays out the cancel button and focuses it (SetFocusToDismantle).
    const c = this.craft?.team === this.wpTeam ? this.craft : null;
    const crafting = this.wpTeam >= 0 && !!c?.crafting;
    if (crafting !== this.menuCrafting) {
      this.menuCrafting = crafting;
      this.updateCurrentLayout();
      if (crafting) this.setFocus(null, this.buttons[0]);
    }
    // cIP2_WP_CraftingButton::LogicalUpdate 0xdc9e80: a valid button's title is grey with bit 0x400
    // (a network game out of turn), red with any of 0x87e, else white.
    for (const b of this.buttons) {
      if (!equipValid(b.equip) || !c) continue;
      const r = b.dismantle ? c.dismantle[b.equip] : b.cancel ? c.cancel : c.craft[b.equip];
      const col = dcol(r & 0x400 ? 0x646464ff : r & 0x87e ? 0x2727c4ff : 0xffffffff);
      Object.assign(b.item.sections.button, { m_TextColourR: col[0], m_TextColourG: col[1], m_TextColourB: col[2], m_TextColourA: col[3] });
    }
    this.renderMenu(t, c);
  }

  /** cIP2_WP_Crafting::Render 0xdcbce0 with a valid parent equip. The first Render loads the
   * textures and draws the 4-button panel whatever the layout chose (the captures show it under
   * three buttons until the next layout). The button panel is top-aligned in the menu. While a
   * crafting button has the focus (a valid variation, or the dismantle button) and the team is
   * not crafting, the ingredients panel goes 8 below it with the four lines of the focused
   * equip's recipe (OrnitonsSerial 16, left-aligned, wrapped in 190x25 boxes 15 right of the
   * centre at 108, 68, 28 and -12 above the panel's centre): "have/need <ingredient>" (red when
   * short) or, dismantling, the salvage "+n <ingredient>" ("0 <ingredient>" for none). */
  private renderMenu(t: HudTeam | undefined, c: CraftChecks | null): void {
    const menu = this.menu, side = this.sidePanel, ing = this.ingredientsPanel, ui = this.ui;
    if (!menu || !side || !ing || !ui) return;
    side.active = ing.active = false;
    for (const l of this.ingredientLines) l.active = false;
    if (!equipValid(this.parentEquip)) return;
    if (!this.menuTextures) {
      this.menuTextures = true;
      this.buttonPanel = CRAFT.panels[2];
    }
    // cRM_Texture m_Width / m_Height: the image's size before the packer's crop (the item's box);
    // Render places the panels by m_Height, the packer's kept area (GetTrueHeight 0xfcc940 is
    // the one that returns the crop's original height): with the original size the ingredients
    // panel sat 7.5 design units (5 px at 720p) lower than in the crafting captures.
    const size = (res: number): [number, number, number] | null => {
      const tx = ui.texture(RES(res));
      return tx ? (tx.crop ? [tx.crop[2], tx.crop[3], tx.sh] : [tx.sw, tx.sh, tx.sh]) : null;
    };
    const ps = this.buttonPanel ? size(this.buttonPanel) : null;
    if (!ps) return;
    [side.w, side.h, side.x, side.y] = [ps[0], ps[1], 0, menu.h / 2 - ps[2] / 2];
    side.resId = RES(this.buttonPanel);
    side.active = true;
    const is = size(CRAFT.ingredients);
    if (this.wpTeam < 0 || !c || c.crafting || !is) return;
    if (!equipValid(this.variation) && !this.variationDismantle) return;
    [ing.w, ing.h, ing.x, ing.y] = [is[0], is[1], 0, side.y - (is[2] / 2 + ps[2] / 2 + 8)];
    ing.active = true;
    const r = this.recipe(this.variation);
    const need = r?.ingredients ?? [0, 0, 0, 0];
    const have = this.variationDismantle ? r?.salvage ?? [0, 0, 0, 0] : t?.ingredients ?? [0, 0, 0, 0];
    [108, 68, 28, -12].forEach((dy, k) => {
      const line = this.ingredientLines[k];
      const name = ui.text(INGREDIENT_TITLES[k]) ?? '';
      line.text = this.variationDismantle ? (have[k] === 0 ? `0 ${name}` : `+${have[k]} ${name}`) : `${have[k]}/${need[k]} ${name}`;
      setColour(line, !this.variationDismantle && need[k] > have[k] ? [0.76862746, 0.15294118, 0.15294118, 1] : [1, 1, 1, 1]);
      [line.x, line.y, line.active] = [15, ing.y + dy, true];
    });
  }

  /** Ammo counts and delays (OrnitonsSerial 16, the ammo left-aligned in white, the delay
   * right-aligned in grey, on the cell's lower edge) and the ingredient counts, over the scene. */
  private drawWeaponsPanelText(h: HudView, p: Ip2Painter): void {
    const f = this.frame;
    if (!f || !f.pActive) return;
    const t: HudTeam | undefined = h.teams[this.wpTeam];
    for (const c of this.cells) {
      if (c.equip === NOTHING || !t || !c.item.pActive) continue;
      const ammo = t.ammo[c.equip], delay = t.delay[c.equip];
      const box = p.boxOf(c.item);
      let shown = ammo !== 0;
      if (delay - 1 < 0xfe && delay > 0 && ammo !== 0) {
        p.text(String(delay), F_ORNITONS16, box, [0.584, 0.584, 0.584, 1], 2, false, false, 0, null);
        shown = true;
      }
      if (ammo !== INFINITE_AMMO && shown) p.text(String(ammo), F_ORNITONS16, box, [1, 1, 1, 1], 0, false, false, 0, null);
    }
    // ingredient counts right of their icons
    this.ingredients.forEach((it, k) => {
      const box = p.boxOf(it);
      p.text(String(t?.ingredients[k] ?? 0), F_ORNITONS16, [box[0] + box[2], box[1], box[2], box[3]], [1, 1, 1, 1], 0, false, false, 0, null);
    });
  }

  /** EquipGetTitle 0xc33e50: "Gui.WeaponPanel.<name>.Prompt". */
  private equipTitle(e: number): string | null {
    const name = this.panelInfo?.equip[e]?.name;
    return name ? this.ui?.text(`Gui.WeaponPanel.${name}.Prompt`) ?? name : null;
  }

  /** cIP2_FocusSystem::SetFocus: the old item's FocusLoss (a crafting button's clears the menu's
   * variation, cIP2_WP_CraftingButton::FocusLoss 0xdca080) and the new one's FocusGain. A cell
   * (cIP2_WP_Cell::FocusGain 0xdcb940, here a mouse-over: no crafting weapon) titles its equip,
   * blank when a cavern does not allow it; a crafting button (FocusGain 0xdc9f50) sets the
   * variation and titles its equip (an upgrade's parent). */
  private setFocus(cell: Cell | null, button: CraftButton | null): void {
    if (cell === this.focus && button === this.focusButton) return;
    if (this.focusButton) {
      this.focusButton.item.state = 'NotFocused';
      this.setCraftingVariation(NOTHING, false);
    }
    this.focus = cell;
    this.focusButton = button;
    if (cell) this.titleEquip = this.cavernAllows(cell.equip) ? cell.equip : NOTHING;
    if (button) {
      button.item.state = 'Focused';
      this.setCraftingVariation(button.equip, button.dismantle);
      const e = equipValid(button.equip) && this.upgraded(button.equip) ? this.recipe(button.equip)?.parent ?? NOTHING : button.equip;
      this.titleEquip = equipValid(e) ? e : NOTHING;
    }
  }

  /** The pointer over the open panel: a mouse-over focuses a cell or a crafting button (moving off
   * keeps the focus, cIP2_2DP::ControllerMouseOver); a press selects a tab (the frame's
   * LogicalUpdate then switches the mode), picks a weapon (weapons mode), sets the crafting
   * menu's weapon (cIP2_WP_Cell::ControllerAccept 0xdca1e0 in crafting mode: SetCraftingWeapon
   * unless a cavern bans the equip; with the mouse the focus stays) or presses a crafting button. */
  private pointer(e: MouseEvent, press: boolean): void {
    const p = this.painter;
    if (!p) return;
    if (this.resultsShown) {
      if (press) this.continueResults();
      return;
    }
    if (!this.panelOpen()) return;
    const b = this.canvas.getBoundingClientRect();
    const x = e.clientX - b.left, y = e.clientY - b.top;
    const inside = (it: Item | null): boolean => {
      if (!it?.pActive) return false;
      const [l, t, w, h] = p.boxOf(it);
      return x >= l && x < l + w && y >= t && y < t + h;
    };
    const cell = [...this.cells, ...this.craftCells].find(c => c.equip !== NOTHING && inside(c.item)) ?? null;
    const button = this.buttons.find(k => inside(k.item)) ?? null;
    if (cell || button) this.setFocus(cell, button);
    if (!press) return;
    if (inside(this.equipTab) || inside(this.craftTab)) {
      const on = inside(this.craftTab);
      if (on !== this.crafting) this.setCraftingMode(on);
      return;
    }
    if (button) this.pressButton(button);
    else if (cell && !this.crafting) {
      // (cIP2_WP_Cell::ControllerAccept 0xdca1e0 picks only while the panel's team is the
      // current team: the worker sends a pick only in a tick this page drives, so a panel opened
      // in another team's turn, an AI's, picks nothing.)
      this.onPick?.(cell.equip, cell.slot);
      this.togglePanel(false);
    } else if (cell && this.cavernAllows(cell.equip)) this.setParentEquip(cell.equip);
  }

  /** cIP2_WP_CraftingButton::ControllerAccept 0xdc9bc0 for the panel team: the press is the sim's
   * MatchEvent::CraftingButton (0x8e; the sim asks CraftingMan, starts the craft, dismantle or
   * cancel, or plays the refusal's Crafting_Reject and comment). The menu's part follows the
   * answer it shows: an accepted craft or dismantle leaves crafting mode (SetCraftingMode(false));
   * UpdateCraftingCells is the cells' refresh. */
  private pressButton(b: CraftButton): void {
    if (!equipValid(b.equip) || this.wpTeam < 0) return;
    this.onCraft?.(this.wpTeam, b.dismantle ? 1 : b.cancel ? 2 : 0, b.equip);
    const c = this.craft?.team === this.wpTeam ? this.craft : null;
    if (c && !b.cancel && (b.dismantle ? c.dismantle[b.equip] : c.craft[b.equip]) === 0) this.setCraftingMode(false);
  }
}
